import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, MessageCircle, Search } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import config from '../../config';
import { useDateFilter } from '../../contexts/DateFilterContext';
import CallManagerMemberPanel from '../../components/CallManagerMemberPanel/CallManagerMemberPanel';
import MemberRowActions from '../../components/MemberRowActions/MemberRowActions';
import CapturedWhatsappModal from '../../components/CapturedWhatsappModal/CapturedWhatsappModal';
import MemberLastCallCell from '../../components/MemberLastCallCell/MemberLastCallCell';
import CustomerCallLogs from '../callLogs/CustomerCallLogs';
import DriverCallLogs from '../callLogs/DriverCallLogs';
import CallLogsAnalytics from '../callLogs/CallLogsAnalytics';
import {
  deleteSegmentFlowBatchMember,
  restoreSegmentFlowBatchMember,
  completeOpsBatch,
  reopenOpsBatch,
  batchManualCompleteConfirmMessage,
  searchCallManagerMembers,
  fetchBatchActionProgress,
  fetchCallManagerStats,
  fetchCallManagerWaStats,
  fetchCallManagerStatsMembers,
  fetchSfBatchDetail,
  fetchSfManualQueue,
  formatDomainCategory,
  formatMemberAddress,
  formatMemberTimestamp,
  formatStatsDisplayDate,
  formatMemberLastActionMain,
  formatMemberLastActionSub,
  attachBatchMemberIds,
  convertMemberFromLead,
  createRefollowBatch,
  fetchRefollowBatches,
  fetchRefollowStats,
  convertNonWhatsappMemberFromLead,
  isNonWhatsappMarketingContext,
  normalizeCapturedWhatsapp,
  formatCapturedWhatsappE164,
  resolveNonWhatsappLeadId,
  batchVoiceTargetType,
  isTerminalOutcome,
  memberEntityLabel,
  memberRowToPanelContext,
  outcomeBadgeTone,
  memberRowTone,
  mergeMemberWaFromPrevious,
} from '../../utils/marketingApi';
import {
  memberListKey,
  shouldAutoOpenCustomerCreated,
} from '../../utils/memberListUx';
import RefollowUpSection from './refollow/RefollowUpSection';
import RefollowCompletedPanel from './refollow/RefollowCompletedPanel';
import RefollowQueueCard from './refollow/RefollowQueueCard';
import CreateRefollowModal from './refollow/CreateRefollowModal';
import RefollowSelectToolbar from './refollow/RefollowSelectToolbar';
import ActionBannerDock from '../../components/ActionBannerDock/ActionBannerDock';
import s from './CallManager.module.scss';

const BATCH_PAGE_SIZE = 5;
const MEMBER_PAGE_SIZE = 20;
const COMPLETED_BATCH_PAGE_SIZE = 5;
const COMPLETED_MEMBER_PAGE_SIZE = 5;
const POLLING_INTERVAL = 90_000;
const STATS_POLL_INTERVAL = 120_000;
const AUTO_REFRESH_MS = POLLING_INTERVAL;
const ACTION_BANNER_MS = 6500;

/** Pending tab — narrow list by open follow-up outcomes (API member_filter values). */
const PENDING_MEMBER_SUB_FILTERS = [
  { key: 'untouched', label: 'Pending' },
  { key: 'follow_up', label: 'Follow up' },
  { key: 'not_contacted', label: 'Not picked up' },
];

/** Actioned tab — terminal outcomes (member_filter + outcome_counts on batch progress). */
const ACTIONED_MEMBER_SUB_FILTERS = [
  { key: 'good_response', label: 'Good response' },
  { key: 'customer_created', label: 'Customer created' },
  { key: 'lead', label: 'Lead' },
  { key: 'drop', label: 'Drop' },
  { key: 'opted_out', label: 'Opt out' },
];

const ACTIONED_OUTCOME_KEYS = new Set([
  ...ACTIONED_MEMBER_SUB_FILTERS.map((f) => f.key),
  'customer_created',
]);

function resolveMemberApiFilter(memberTab, memberSubFilter) {
  const sub = (memberSubFilter || '').trim().toLowerCase();
  if (sub) return sub;
  return memberTab === 'actioned' ? 'actioned' : 'pending';
}

function memberEligibleForRefollow(member) {
  return Boolean(member?.customer_id);
}

const REFOLLOW_OPEN_PENDING_OUTCOMES = new Set(['follow_up', 'not_contacted']);

function memberMatchesSubFilterForRefollow(member, filterKey) {
  if (!memberEligibleForRefollow(member)) return false;
  const f = (filterKey || '').trim().toLowerCase();
  if (!f) return true;
  const oc = String(member?.outcome || '').toLowerCase();
  if (f === 'pending') {
    if (!oc) return true;
    if (REFOLLOW_OPEN_PENDING_OUTCOMES.has(oc)) return true;
    return false;
  }
  if (f === 'untouched') return !oc;
  if (f === 'actioned') {
    if (!oc || REFOLLOW_OPEN_PENDING_OUTCOMES.has(oc)) return false;
    return ACTIONED_OUTCOME_KEYS.has(oc);
  }
  if (f === 'customer_created') return oc === 'customer_created';
  return oc === f;
}

function refollowApiFilterForPicker({ inCompleted, completedOutcomeFilter, memberApiFilter }) {
  if (inCompleted) {
    return completedOutcomeFilter === 'all' ? null : completedOutcomeFilter;
  }
  return memberApiFilter || null;
}

function refollowMatchFilterKey({ inCompleted, completedOutcomeFilter, memberApiFilter }) {
  if (inCompleted) {
    return completedOutcomeFilter === 'all' ? '' : completedOutcomeFilter;
  }
  return memberApiFilter || '';
}

function memberListIsActionedView(memberTab, memberSubFilter) {
  const sub = (memberSubFilter || '').trim().toLowerCase();
  if (sub) return ACTIONED_OUTCOME_KEYS.has(sub);
  return memberTab === 'actioned';
}

function outcomeCountFromProgress(progress, outcomeKey) {
  if (!outcomeKey) return 0;
  const oc = progress?.outcome_counts;
  if (oc && Object.prototype.hasOwnProperty.call(oc, outcomeKey)) {
    return Number(oc[outcomeKey] || 0);
  }
  if (outcomeKey === 'untouched') return Number(progress?.untouched_count || 0);
  if (outcomeKey === 'follow_up') return Number(progress?.follow_up_count || 0);
  if (outcomeKey === 'not_contacted') return Number(progress?.not_contacted_count || 0);
  return 0;
}

const COMPLETED_OUTCOME_STAT_KEYS = [
  { key: 'good_response', label: 'Good response', tone: 'success' },
  { key: 'customer_created', label: 'Customer created', tone: 'success' },
  { key: 'lead', label: 'Lead', tone: 'lead' },
  { key: 'follow_up', label: 'Follow up', tone: 'followup' },
  { key: 'not_contacted', label: 'Not picked up', tone: 'attempted' },
  { key: 'opted_out', label: 'Opt out', tone: 'muted' },
  { key: 'drop', label: 'Drop', tone: 'danger' },
];

function completedOutcomeStatItems(progress, { showZeros = false } = {}) {
  const oc = progress?.outcome_counts || {};
  const rows = COMPLETED_OUTCOME_STAT_KEYS.map(({ key, label, tone }) => ({
    key,
    label,
    tone,
    count: Number(oc[key]) || 0,
  }));
  if (rows.some((r) => r.count > 0)) {
    return showZeros ? rows : rows.filter((row) => row.count > 0);
  }
  const fallback = [];
  if (progress?.follow_up_count) {
    fallback.push({ key: 'follow_up', label: 'Follow up', tone: 'followup', count: progress.follow_up_count });
  }
  if (progress?.not_contacted_count) {
    fallback.push({
      key: 'not_contacted',
      label: 'Not picked up',
      tone: 'attempted',
      count: progress.not_contacted_count,
    });
  }
  return fallback;
}

function CompletedBatchCountLine({ total, actioned, pending }) {
  return (
    <div className={s.completedCountLine}>
      <span>Total <strong>{total}</strong></span>
      <span className={s.completedItemCountSep}>·</span>
      <span>Actioned <strong>{actioned}</strong></span>
      <span className={s.completedItemCountSep}>·</span>
      <span>Pending <strong>{pending}</strong></span>
    </div>
  );
}

function CompletedBatchSummaryRow({ progress, batch, showTitle = true, showOutcomes = true, children = null }) {
  const { total, actioned, pending } = completedBatchCounts(progress, batch);
  const dateLabel = progress?.ops_completed_at || batch?.escalated_at || batch?.created_at;
  return (
    <div className={s.completedSummaryRow}>
      <div className={s.completedItemInner}>
        <div className={s.completedItemMain}>
          {showTitle && batch?.id ? (
            <div className={s.completedItemTitle}>
              <span className={s.batchId}>#{batch.id}</span>
              <span className={s.batchName}>{batch.name || 'Batch'}</span>
            </div>
          ) : null}
          <CompletedBatchCountLine total={total} actioned={actioned} pending={pending} />
          {dateLabel ? (
            <div className={s.completedItemDate}>{formatMemberTimestamp(dateLabel)}</div>
          ) : null}
          {children}
        </div>
        {showOutcomes ? (
          <div className={s.completedItemStats}>
            <CompletedBatchOutcomeStats progress={progress} compact />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CompletedBatchOutcomeStats({ progress, compact = false, showZeros = false }) {
  const items = completedOutcomeStatItems(progress, { showZeros: showZeros || compact });
  if (!items.length) {
    return compact ? (
      <span className={s.completedOutcomeEmpty}>—</span>
    ) : (
      <p className={s.completedOutcomeEmpty}>Outcome breakdown loads after customer API update.</p>
    );
  }
  return (
    <div className={`${s.completedOutcomeGrid}${compact ? ` ${s.completedOutcomeGridCompact}` : ''}`}>
      {compact ? <span className={s.completedOutcomeHeading}>Outcomes</span> : null}
      {items.map(({ key, label, count, tone }) => (
        <span key={key} className={s.completedOutcomeChip} data-tone={tone}>
          <strong>{count}</strong>
          <span>{label}</span>
        </span>
      ))}
    </div>
  );
}

function CompletedOutcomeFilterBar({ progress, activeFilter, onSelect }) {
  const oc = progress?.outcome_counts || {};
  const allTotal = progress?.active_total ?? progress?.total ?? 0;
  return (
    <div className={`${s.completedFilterBar} ${s.completedFilterBarNested}`} role="tablist" aria-label="Filter members by outcome">
      <button
        type="button"
        role="tab"
        aria-selected={activeFilter === 'all'}
        className={`${s.completedFilterChip}${activeFilter === 'all' ? ` ${s.completedFilterChipActive}` : ''}`}
        onClick={() => onSelect('all')}
      >
        All <strong>{allTotal}</strong>
      </button>
      {COMPLETED_OUTCOME_STAT_KEYS.map(({ key, label, tone }) => {
        const count = Number(oc[key]) || 0;
        if (!count) return null;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeFilter === key}
            className={`${s.completedFilterChip}${activeFilter === key ? ` ${s.completedFilterChipActive}` : ''}`}
            data-tone={tone}
            onClick={() => onSelect(key)}
          >
            {label} <strong>{count}</strong>
          </button>
        );
      })}
    </div>
  );
}

function queueProgressFromBatch(batch) {
  return {
    total: batch?.progress_total ?? batch?.member_count ?? 0,
    actioned: batch?.actioned ?? 0,
    pending: batch?.pending ?? 0,
  };
}

function completedBatchCounts(progress, batch) {
  const p = progress || {};
  const total = p.active_total ?? p.total ?? batch?.member_count ?? 0;
  return {
    total,
    actioned: p.actioned ?? 0,
    pending: p.pending ?? 0,
  };
}

function BatchSummaryBlock({
  batchId,
  name,
  description,
  total,
  actioned,
  createdAt,
  compact = false,
}) {
  return (
    <div className={`${s.batchSummaryStack}${compact ? ` ${s.batchSummaryStackCompact}` : ''}`}>
      <div className={s.batchTitleRow}>
        <span className={s.batchId}>#{batchId}</span>
        <span className={s.batchName}>{name || `Batch #${batchId}`}</span>
      </div>
      {!compact ? (
        <div className={`${s.batchDescription}${description ? '' : ` ${s.batchDescriptionEmpty}`}`}>
          {description || 'No description provided'}
        </div>
      ) : description ? (
        <div className={s.batchDescription}>{description}</div>
      ) : null}
      <div className={s.batchMetaRow}>
        <div className={s.batchMetaLeft}>
          {createdAt ? formatMemberTimestamp(createdAt) : ''}
        </div>
        <div className={s.batchRightActioned}>
          <strong>{actioned}</strong>
          <span className={s.batchProgressStatSep}>/</span>
          {total}
        </div>
      </div>
    </div>
  );
}

function fmtCell(v) {
  if (v == null || v === '') return '—';
  return String(v);
}

function opsAuthToken() {
  return localStorage.getItem('token') || 'mock-jwt-token-for-testing';
}

function sortMembers(arr) {
  return [...arr].sort((a, b) => {
    if (a.star !== b.star) return (b.star ? 1 : 0) - (a.star ? 1 : 0);
    const aTime = a.outcome_at ? new Date(a.outcome_at).getTime() : 0;
    const bTime = b.outcome_at ? new Date(b.outcome_at).getTime() : 0;
    return aTime - bTime;
  });
}

/** Pending tab: show Follow up / Not picked up etc.; untouched rows stay "Pending". */
function memberOutcomeBadge(member, isActioned) {
  const oc = String(member?.outcome || '').toLowerCase();
  const showRecorded = isActioned
    || ['follow_up', 'not_contacted', 'customer_created'].includes(oc)
    || (oc && oc !== 'pending');
  if (showRecorded) {
    return {
      label: fmtCell(member.outcome_label || member.outcome || 'Pending'),
      tone: outcomeBadgeTone(member.outcome || oc),
    };
  }
  return { label: 'Pending', tone: 'pending' };
}

function BatchCompleteConfirmModal({ open, progress, onCancel, onConfirm, confirming }) {
  if (!open || !progress) return null;

  const followUps = progress.follow_up_count ?? 0;
  const notPicked = progress.not_contacted_count ?? 0;
  const openFollow = progress.open_followups ?? followUps + notPicked;
  const needsConfirm = openFollow > 0;
  const summaryLine = needsConfirm
    ? batchManualCompleteConfirmMessage(progress)
    : 'Every member has an action recorded. Mark this batch completed? It will move to View Completed for audit.';

  return createPortal(
    <div
      className={s.completeAlertBackdrop}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="cm-complete-alert-title"
      aria-describedby="cm-complete-alert-desc"
      onClick={(e) => {
        if (e.target === e.currentTarget && !confirming) onCancel();
      }}
    >
      <div
        className={`${s.completeAlertCard}${needsConfirm ? ` ${s.completeAlertCardWarn}` : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.completeAlertIconWrap} data-tone={needsConfirm ? 'warn' : 'confirm'}>
          {needsConfirm ? <AlertTriangle size={28} strokeWidth={2} aria-hidden /> : null}
          {!needsConfirm ? <span className={s.completeAlertIconCheck} aria-hidden>✓</span> : null}
        </div>
        <h3 id="cm-complete-alert-title" className={s.completeAlertTitle}>
          Mark batch completed?
        </h3>
        <p id="cm-complete-alert-desc" className={s.completeAlertMessage}>
          {summaryLine}
        </p>
        {needsConfirm ? (
          <ul className={s.completeAlertStats}>
            {followUps > 0 ? (
              <li><strong>{followUps}</strong> follow-up{followUps === 1 ? '' : 's'} still on Pending</li>
            ) : null}
            {notPicked > 0 ? (
              <li><strong>{notPicked}</strong> not picked up still on Pending</li>
            ) : null}
          </ul>
        ) : null}
        <div className={s.completeAlertActions}>
          <button
            type="button"
            className={s.completeAlertCancel}
            disabled={confirming}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={s.completeAlertConfirm}
            disabled={confirming}
            onClick={onConfirm}
          >
            {confirming ? 'Completing…' : 'Yes, mark completed'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CallManagerReadOnlyMemberRow({
  member,
  batch = null,
  refollowSelectMode,
  refollowSelected,
  onToggleRefollowSelect,
}) {
  const entity = memberEntityLabel(member, batch);
  const outcomeLabel = fmtCell(member.outcome_label || member.outcome || 'Pending');
  const badgeTone = member.outcome ? outcomeBadgeTone(member.outcome) : 'pending';
  const rowTone = memberRowTone(member.outcome);
  const rowToneClass = {
    success: s.memberCardToneSuccess,
    followup: s.memberCardToneFollowup,
    lead: s.memberCardToneLead,
    attempted: s.memberCardToneAttempted,
    muted: s.memberCardToneMuted,
    deleted: s.memberCardToneDeleted,
    danger: s.memberCardToneDanger,
    neutral: '',
  }[rowTone] || '';

  const memberRowId = member.id || member.batch_member_id;
  const refollowEligible = memberEligibleForRefollow(member);

  return (
    <article
      className={`${s.memberCard} ${s.memberCardReadOnly} ${s.memberCardCompletedAudit}${rowToneClass ? ` ${rowToneClass}` : ''}`}
      onClick={() => {
        if (refollowSelectMode && refollowEligible) {
          onToggleRefollowSelect?.(memberRowId, member);
        }
      }}
    >
      <div className={s.memberCardMain}>
        <div className={`${s.memberCardTop}${refollowSelectMode ? ` ${s.memberCardTopSelect}` : ''}`}>
          <div className={s.memberCardTopLead}>
            {refollowSelectMode ? (
              <label className={s.memberSelectCell} onClick={(e) => e.stopPropagation()}>
                {refollowEligible ? (
                  <input
                    type="checkbox"
                    checked={Boolean(refollowSelected)}
                    aria-label={`Select ${member.name || 'member'} for follow-up`}
                    onChange={() => onToggleRefollowSelect?.(memberRowId, member)}
                  />
                ) : null}
              </label>
            ) : null}
            <div className={s.memberNameBlock}>
              {member.star ? <span className={s.starIcon} title="Star customer">★</span> : null}
              <h3 className={s.memberName}>{fmtCell(member.name)}</h3>
            </div>
          </div>
          <span className={s.outcomeBadge} data-tone={badgeTone}>{outcomeLabel}</span>
        </div>
        <div className={s.completedMemberMeta}>
          <div className={s.completedMemberMetaRow}>
            <span className={s.completedMemberMetaPair}>
              <span className={s.metaLabel}>Phone</span>
              <span className={s.metaValue}>{fmtCell(member.phone_e164 || member.phone)}</span>
            </span>
            <span className={s.completedMemberMetaPair}>
              <span className={s.metaLabel}>ID</span>
              <span className={`${s.metaValue} ${s.entityBadge}`} data-type={entity.type}>{entity.label}</span>
            </span>
          </div>
          <div className={s.completedMemberMetaRow}>
            <span className={s.completedMemberMetaPair}>
              <span className={s.metaLabel}>Domain · Category</span>
              <span className={s.metaValue}>{formatDomainCategory(member)}</span>
            </span>
          </div>
          <div className={s.completedMemberMetaRow}>
            <span className={s.completedMemberMetaPair}>
              <span className={s.metaLabel}>Last action</span>
              <span className={s.metaValue}>{formatMemberLastActionMain(member)}</span>
              {formatMemberLastActionSub(member) ? (
                <span className={s.metaSub}>{formatMemberLastActionSub(member)}</span>
              ) : null}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function ActionConfirmationBanner({ action, onEdit }) {
  if (!action) return null;
  const tone = outcomeBadgeTone(action.outcome);
  return (
    <div className={s.actionBanner} data-tone={tone} role="status" aria-live="polite">
      <div className={s.actionBannerMain}>
        <span className={s.actionBannerIcon} aria-hidden>✓</span>
        <div className={s.actionBannerText}>
          <span className={s.actionBannerTitle}>{action.outcomeLabel} saved</span>
          <span className={s.actionBannerMeta}>
            {action.memberName || 'Member'} · {action.phone || '—'}
          </span>
        </div>
      </div>
      <button type="button" className={s.actionBannerEditBtn} onClick={onEdit}>
        Edit status
      </button>
    </div>
  );
}

function phoneLast10(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
}

function CallManagerMemberRow({
  member,
  batchId,
  batch = null,
  isActioned,
  isFocused,
  recentActionKey,
  onOpenPanel,
  onQuickCreateCustomer,
  onRefresh,
  onOutcomeChange,
  onDelete,
  onRestore,
  showToast,
  refollowSelectMode,
  refollowSelected,
  onToggleRefollowSelect,
}) {
  const entity = memberEntityLabel(member, batch);
  const memberKey = memberListKey(member);
  const bannerActiveForRow = recentActionKey && recentActionKey === memberKey;
  const { label: outcomeLabel, tone: badgeTone } = memberOutcomeBadge(member, isActioned);
  const rowTone = memberRowTone(member.outcome);
  const rowToneClass = {
    success: s.memberCardToneSuccess,
    followup: s.memberCardToneFollowup,
    lead: s.memberCardToneLead,
    attempted: s.memberCardToneAttempted,
    muted: s.memberCardToneMuted,
    deleted: s.memberCardToneDeleted,
    danger: s.memberCardToneDanger,
    neutral: s.memberCardToneNeutral,
  }[rowTone] || '';

  const memberRowId = member.id || member.batch_member_id;
  const refollowEligible = memberEligibleForRefollow(member);
  const lastContactsRef = useRef(null);

  return (
    <article
      data-member-id={member.id || member.batch_member_id || ''}
      data-phone-last10={phoneLast10(member.phone_e164 || member.phone)}
      className={`${s.memberCard}${rowToneClass ? ` ${rowToneClass}` : ''}${isFocused ? ` ${s.memberCardFocus}` : ''}`}
      onClick={(e) => {
        if (e.target.closest(`.${s.lastContactsHit}`)) return;
        if (e.target.closest('[data-call-dropdown-panel]')) return;
        if (refollowSelectMode && refollowEligible) {
          onToggleRefollowSelect?.(memberRowId, member);
          return;
        }
        onOpenPanel(member);
      }}
    >
      <div className={s.memberCardMain}>
        <div className={`${s.memberCardTop}${refollowSelectMode ? ` ${s.memberCardTopSelect}` : ''}`}>
          <div className={s.memberCardTopLead}>
            {refollowSelectMode ? (
              <label
                className={s.memberSelectCell}
                onClick={(e) => e.stopPropagation()}
              >
                {refollowEligible ? (
                  <input
                    type="checkbox"
                    checked={Boolean(refollowSelected)}
                    aria-label={`Select ${member.name || 'member'} for follow-up`}
                    onChange={() => onToggleRefollowSelect?.(memberRowId, member)}
                  />
                ) : null}
              </label>
            ) : null}
            <div className={s.memberNameBlock}>
              {member.star ? <span className={s.starIcon} title="Star customer">★</span> : null}
              <h3 className={s.memberName}>{fmtCell(member.name)}</h3>
            </div>
          </div>
          <span className={s.outcomeBadge} data-tone={badgeTone}>{outcomeLabel}</span>
        </div>

        <div className={s.memberMetaGrid}>
          <div className={s.metaItem}>
            <span className={s.metaLabel}>Phone</span>
            <span className={s.metaValue}>{fmtCell(member.phone_e164 || member.phone)}</span>
          </div>
          <div className={s.metaItem}>
            <span className={s.metaLabel}>ID</span>
            <span className={`${s.metaValue} ${s.entityBadge}`} data-type={entity.type}>{entity.label}</span>
          </div>
          <div className={s.metaItem}>
            <span className={s.metaLabel}>Domain · Category</span>
            <span className={s.metaValue}>{formatDomainCategory(member)}</span>
          </div>
          <div className={s.metaItem}>
            <span className={s.metaLabel}>Address · City · Pincode</span>
            <span className={s.metaValue}>{formatMemberAddress(member)}</span>
          </div>
          <div
            className={`${s.metaItem} ${s.lastCalledMeta} ${s.lastContactsHit}`}
            onClick={(e) => {
              e.stopPropagation();
              if (e.target.closest('[data-call-dropdown-panel]')) return;
              if (e.target.closest('[data-member-last-call]')) return;
              if (e.target.closest('button')) return;
              lastContactsRef.current?.toggleOpen();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                lastContactsRef.current?.toggleOpen();
              }
            }}
            role="presentation"
          >
            <span className={s.metaLabel}>Last contacts</span>
            <MemberLastCallCell
              ref={lastContactsRef}
              phone={member.phone_e164 || member.phone}
              lastCall={member.last_manual_call ?? member.last_call}
              lastManualCall={member.last_manual_call ?? member.last_call}
              lastBotCall={member.last_bot_call ?? null}
              lastWa={member.last_wa ?? null}
              fixedContactRows
              leadId={member.lead_id}
              customerId={member.customer_id}
              compact
              serverPaginatedHistory
              delegateToggleToParent
            />
          </div>
          <div className={`${s.metaItem} ${s.lastActionMeta}`}>
            <span className={s.metaLabel}>Last action</span>
            <span className={s.metaValue}>{formatMemberLastActionMain(member)}</span>
            {formatMemberLastActionSub(member) ? (
              <span className={s.metaSub}>{formatMemberLastActionSub(member)}</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className={s.memberCardActions}>
        <MemberRowActions
          batchId={batchId}
          batch={batch}
          member={member}
          marketingInitiated={false}
          showCall
          showToast={showToast}
          quietStatusToasts
          compactStatusStrip={!isActioned}
          suppressEditButton={bannerActiveForRow}
          onOpenPanel={(m) => onOpenPanel({ ...m, batch_id: batchId })}
          onQuickCreateCustomer={onQuickCreateCustomer}
          onRefresh={onRefresh}
          onOutcomeChange={(patch) => onOutcomeChange?.(member.id, patch, member.batch_id || batchId)}
          onDelete={onDelete ? (m) => onDelete(m) : null}
          onRestore={onRestore ? (m) => onRestore(m) : null}
        />
      </div>
    </article>
  );
}

function CallManagerMemberList({
  members,
  batchId,
  batch = null,
  isActionedView,
  focusMember,
  recentActionKey,
  onOpenPanel,
  onQuickCreateCustomer,
  onRefresh,
  onOutcomeChange,
  onDelete,
  onRestore,
  showToast,
  refollowSelectMode,
  refollowSelectedIds,
  onToggleRefollowSelect,
}) {
  const selectedSet = refollowSelectedIds instanceof Set
    ? refollowSelectedIds
    : new Set(refollowSelectedIds || []);
  return (
    <div className={s.memberList}>
      {members.map((m) => {
        const mid = m.id || m.batch_member_id;
        return (
          <CallManagerMemberRow
            key={m.phone_e164 || m.id}
            member={m}
            batchId={batchId}
            batch={batch}
            isActioned={isActionedView}
            isFocused={Boolean(
              focusMember
              && (
                (focusMember.batchMemberId && Number(m.id || m.batch_member_id) === Number(focusMember.batchMemberId))
                || (focusMember.phoneLast10 && phoneLast10(m.phone_e164 || m.phone) === focusMember.phoneLast10)
              )
            )}
            recentActionKey={recentActionKey}
            onOpenPanel={onOpenPanel}
            onQuickCreateCustomer={onQuickCreateCustomer}
            onRefresh={onRefresh}
            onOutcomeChange={onOutcomeChange}
            onDelete={onDelete}
            onRestore={onRestore}
            showToast={showToast}
            refollowSelectMode={refollowSelectMode}
            refollowSelected={selectedSet.has(mid)}
            onToggleRefollowSelect={onToggleRefollowSelect}
          />
        );
      })}
    </div>
  );
}

const getTodayStr = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getThisWeekRange = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diffToMonday = (dayOfWeek + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - diffToMonday);

  const fmt = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return {
    from: fmt(monday),
    to: fmt(new Date()),
  };
};

const STATS_MEMBER_PAGE_SIZE = 20;

function statsFilterRowClass(row) {
  const tone = [row?.row_tone, row?.action_label, row?.outcome, row?.action_display]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (tone.includes('good')) return 'statsRowGood';
  if (tone.includes('follow')) return 'statsRowFollow';
  if (
    tone.includes('not_picked')
    || tone.includes('not picked')
    || tone.includes('not_contacted')
    || tone.includes('not contacted')
  ) return 'statsRowAttempted';
  if (tone.includes('lead')) return 'statsRowLead';
  if (tone.includes('customer')) return 'statsRowCustomer';
  if (tone.includes('opt')) return 'statsRowOptout';
  if (tone.includes('drop')) return 'statsRowDrop';
  if (String(row?.wa_status || '').toLowerCase() === 'failed') return 'statsRowFailed';
  return 'statsRowDefault';
}

const STATS_FILTER_LABELS = {
  total: 'Total — unique customers (last ops action in range)',
  good_response: 'Good response',
  customers_created: 'Customers created',
  follow_ups: 'Follow ups',
  leads: 'Leads',
  not_picked_up: 'Not picked up',
  dropped: 'Dropped',
  opted_out: 'Opted out',
  wa_sent: 'WA sent (Call Manager)',
  wa_delivered: 'WA delivered',
  wa_read: 'WA read',
  wa_replied: 'WA replied',
  wa_failed: 'WA failed',
};

const CallManagerPage = () => {
  const location = useLocation();
  const { dateRange, clearDateFilter, setToday } = useDateFilter();
  const startDate = dateRange?.startDate || '';
  const endDate = dateRange?.endDate || '';
  const statsStart = startDate || new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  const statsEnd = endDate || startDate || statsStart;

  // Dedicated common date filter state for all Call Logs tabs (default: 'today')
  const [callLogsDatePreset, setCallLogsDatePreset] = useState('today');
  const [callLogsFromDate, setCallLogsFromDate] = useState(() => getTodayStr());
  const [callLogsToDate, setCallLogsToDate] = useState(() => getTodayStr());

  const handleSetCallLogsPreset = (preset) => {
    setCallLogsDatePreset(preset);
    if (preset === 'today') {
      const today = getTodayStr();
      setCallLogsFromDate(today);
      setCallLogsToDate(today);
    } else if (preset === 'yesterday') {
      const yest = getYesterdayStr();
      setCallLogsFromDate(yest);
      setCallLogsToDate(yest);
    } else if (preset === 'this_week') {
      const { from, to } = getThisWeekRange();
      setCallLogsFromDate(from);
      setCallLogsToDate(to);
    } else if (preset === 'all') {
      setCallLogsFromDate('');
      setCallLogsToDate('');
    } else if (preset === 'custom') {
      if (!callLogsFromDate && !callLogsToDate) {
        const today = getTodayStr();
        setCallLogsFromDate(today);
        setCallLogsToDate(today);
      }
    }
  };

  const handleCustomDateChange = (from, to) => {
    setCallLogsDatePreset('custom');
    setCallLogsFromDate(from);
    setCallLogsToDate(to);
  };
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [batchPage, setBatchPage] = useState(1);
  const [batchMeta, setBatchMeta] = useState({ total: 0, total_pages: 1 });
  const [batches, setBatches] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [selectedBatchName, setSelectedBatchName] = useState('');
  const [selectedBatchDescription, setSelectedBatchDescription] = useState('');
  const [selectedBatchCreatedAt, setSelectedBatchCreatedAt] = useState('');
  const [selectedBatchSource, setSelectedBatchSource] = useState('');
  const [selectedBatchVoiceTarget, setSelectedBatchVoiceTarget] = useState('');
  const [selectedBatchProgress, setSelectedBatchProgress] = useState(null);
  const [nwCaptureModal, setNwCaptureModal] = useState(null);
  const [nwCaptureBusy, setNwCaptureBusy] = useState(false);
  const [memberTab, setMemberTab] = useState('pending');
  const [memberSubFilter, setMemberSubFilter] = useState('');
  const memberApiFilter = useMemo(
    () => resolveMemberApiFilter(memberTab, memberSubFilter),
    [memberTab, memberSubFilter],
  );
  const memberListActioned = useMemo(
    () => memberListIsActionedView(memberTab, memberSubFilter),
    [memberTab, memberSubFilter],
  );
  const [memberPage, setMemberPage] = useState(1);
  const [memberMeta, setMemberMeta] = useState({ total: 0, total_pages: 1 });
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersRefreshing, setMembersRefreshing] = useState(false);
  const [focusMember, setFocusMember] = useState(null);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMember, setPanelMember] = useState(null);
  const [panelStackAboveOverlay, setPanelStackAboveOverlay] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showRefollowCompleted, setShowRefollowCompleted] = useState(false);
  const [completedViewBatch, setCompletedViewBatch] = useState(null);
  const [completedBatchesList, setCompletedBatchesList] = useState([]);
  const [completedBatchMeta, setCompletedBatchMeta] = useState({ total: 0, total_pages: 1 });
  const [completedBatchPage, setCompletedBatchPage] = useState(1);
  const [completedMembersPage, setCompletedMembersPage] = useState(1);
  const [completedMemberMeta, setCompletedMemberMeta] = useState({ total: 0, total_pages: 1 });
  const [reopenConfirming, setReopenConfirming] = useState(false);
  const [completedMembers, setCompletedMembers] = useState([]);
  const [completedMembersLoading, setCompletedMembersLoading] = useState(false);
  const [completedViewProgress, setCompletedViewProgress] = useState(null);
  const [completedOutcomeFilter, setCompletedOutcomeFilter] = useState('all');
  const [activeView, setActiveView] = useState('batches'); // 'batches' | 'refollow' | call-log views
  const [refollowSelectMode, setRefollowSelectMode] = useState(false);
  const [refollowSelectedIds, setRefollowSelectedIds] = useState(() => new Set());
  const [refollowSelectedCache, setRefollowSelectedCache] = useState(() => new Map());
  const [refollowModalOpen, setRefollowModalOpen] = useState(false);
  const [refollowSubmitting, setRefollowSubmitting] = useState(false);
  const [refollowStats, setRefollowStats] = useState({
    due_today: 0,
    done_today: 0,
    cancelled_today: 0,
    upcoming: 0,
    draft_batches: 0,
    wa_sent: 0,
    wa_delivered: 0,
    wa_read: 0,
    wa_replied: 0,
    wa_failed: 0,
  });
  const [refollowSummaryLoading, setRefollowSummaryLoading] = useState(false);
  const [refollowQueueTab, setRefollowQueueTab] = useState('due_today');
  const [refollowRefreshTick, setRefollowRefreshTick] = useState(0);
  const [refollowCheckAllBusy, setRefollowCheckAllBusy] = useState(false);
  const [callLogsSearchTerm, setCallLogsSearchTerm] = useState('');
  const [recentAction, setRecentAction] = useState(null);
  const [holdCustomerCreatedKeys, setHoldCustomerCreatedKeys] = useState(() => new Set());
  const [panelOpenKey, setPanelOpenKey] = useState(null);
  const [panelActivityKey, setPanelActivityKey] = useState(0);
  const [nextRefreshIn, setNextRefreshIn] = useState(POLLING_INTERVAL / 1000);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeConfirming, setCompleteConfirming] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [waStatsOpen, setWaStatsOpen] = useState(false);
  const [statsFilterBucket, setStatsFilterBucket] = useState(null);
  const [statsFilterPage, setStatsFilterPage] = useState(1);
  const [statsFilterData, setStatsFilterData] = useState(null);
  const [statsFilterLoading, setStatsFilterLoading] = useState(false);
  const waStatsRef = useRef(null);
  const waStatsReqRef = useRef(0);
  const membersReqRef = useRef(0);
  const batchCountsReqRef = useRef(0);
  const refreshTimerRef = useRef(null);
  const recentActionTimerRef = useRef(null);
  const prevOutcomesRef = useRef(new Map());
  const autoOpenedCustomerCreatedRef = useRef(new Set());

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    if (type !== 'error') setError(null);
    else setError(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3500);
  };

  /** Count-only SQL (action-progress) — sub-filter badges; never loads member rows / WA enrich. */
  const refreshBatchMemberCounts = useCallback(async (batchId) => {
    if (!batchId) return;
    const reqId = ++batchCountsReqRef.current;
    const prog = await fetchBatchActionProgress(batchId).catch(() => null);
    if (reqId !== batchCountsReqRef.current || !prog?.ok) return;
    setSelectedBatchProgress(prog);
    setProgress((prev) => ({ ...prev, [batchId]: prog }));
  }, []);

  const loadStats = useCallback(async ({ fresh = false } = {}) => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await fetchCallManagerStats({
        startDate: statsStart,
        endDate: statsEnd,
        fresh,
        skipWa: true,
      });
      setStats({ ...data, wa_loading: true, wa_error: false });
      const waReqId = ++waStatsReqRef.current;
      const waDeadline = 50_000;
      Promise.race([
        fetchCallManagerWaStats({ startDate: statsStart, endDate: statsEnd, fresh }),
        new Promise((_, reject) => {
          window.setTimeout(() => reject(new Error('wa_stats_timeout')), waDeadline);
        }),
      ])
        .then((wa) => {
          if (waReqId !== waStatsReqRef.current) return;
          setStats((prev) => (prev ? { ...prev, ...wa, wa_loading: false, wa_error: false } : prev));
        })
        .catch(() => {
          if (waReqId !== waStatsReqRef.current) return;
          setStats((prev) => (prev ? { ...prev, wa_loading: false, wa_error: true } : prev));
        });
    } catch (err) {
      setStatsError(err?.message || 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  }, [statsStart, statsEnd]);

  const loadStatsFilter = useCallback(async (bucket, page = 1) => {
    if (!bucket) {
      setStatsFilterData(null);
      return;
    }
    setStatsFilterLoading(true);
    try {
      const data = await fetchCallManagerStatsMembers({
        bucket,
        startDate: statsStart,
        endDate: statsEnd,
        page,
        pageSize: STATS_MEMBER_PAGE_SIZE,
      });
      if (data?.ok === false) {
        setStatsFilterData({ items: [], total: 0, total_pages: 1 });
        showToast(data.error || 'Could not load stats list', 'error');
        return;
      }
      setStatsFilterData(data);
    } catch (err) {
      setStatsFilterData(null);
      showToast(err.message || 'Could not load stats list', 'error');
    } finally {
      setStatsFilterLoading(false);
    }
  }, [statsStart, statsEnd]);

  const toggleStatsFilter = useCallback((bucket) => {
    setWaStatsOpen(false);
    setStatsFilterBucket((prev) => {
      if (prev === bucket) {
        setStatsFilterData(null);
        return null;
      }
      setStatsFilterPage(1);
      return bucket;
    });
  }, []);

  useEffect(() => {
    if (!statsFilterBucket) return;
    loadStatsFilter(statsFilterBucket, statsFilterPage);
  }, [statsFilterBucket, statsFilterPage, loadStatsFilter]);

  useEffect(() => {
    setStatsFilterBucket(null);
    setStatsFilterData(null);
    setStatsFilterPage(1);
  }, [statsStart, statsEnd]);

  const loadBatches = useCallback(async (page = batchPage) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSfManualQueue({
        page,
        pageSize: BATCH_PAGE_SIZE,
        completionSource: 'ops_queue',
        escalated: true,
        opsOpenOnly: true,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const rows = data?.batches || [];
      setBatches(rows);
      setBatchMeta({ total: data?.total || 0, total_pages: data?.total_pages || 1 });
      const prog = {};
      rows.forEach((b) => { if (b?.id) prog[b.id] = queueProgressFromBatch(b); });
      setProgress((prev) => ({ ...prev, ...prog }));
    } catch (err) {
      setError(err.message || 'Failed to load batches');
      setBatches([]);
    } finally {
      setLoading(false);
      setNextRefreshIn(POLLING_INTERVAL / 1000);
    }
  }, [batchPage, startDate, endDate]);

  const cmBatchHomeSearchActive = activeView === 'batches' && !selectedBatchId;

  useEffect(() => {
    if (!cmBatchHomeSearchActive) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }
    const q = memberSearch.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await searchCallManagerMembers(q);
        if (!cancelled) setSearchResults(data?.items || []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [memberSearch, cmBatchHomeSearchActive]);

  const loadMembers = useCallback(async (batchId, page = memberPage, { silent = false, filter = memberApiFilter } = {}) => {
    if (!batchId) return;
    const reqId = ++membersReqRef.current;
    if (silent) setMembersRefreshing(true);
    else setMembersLoading(true);
    setError(null);
    try {
      const data = await fetchSfBatchDetail(batchId, {
        page,
        pageSize: focusMember ? 50 : MEMBER_PAGE_SIZE,
        memberFilter: filter,
      });
      if (reqId !== membersReqRef.current) return;
      const batchVoiceTarget = data?.batch?.voice_target_type || '';
      const batchCompletionSource = data?.batch?.completion_source || '';
      const rows = attachBatchMemberIds(
        data?.member_live || data?.batch?.members || [],
        data?.batch?.members || [],
      );
      setMembers((prev) => {
        const prevById = new Map((prev || []).map((m) => [m.id, m]));
        return sortMembers(rows.map((m) => {
          const base = {
            ...m,
            batch_id: batchId,
            batch_voice_target: batchVoiceTarget,
            batch_completion_source: batchCompletionSource,
          };
          const prior = prevById.get(m.id);
          return prior ? mergeMemberWaFromPrevious(prior, base) : base;
        }));
      });
      setMemberMeta({
        total: data?.members_total || rows.length,
        total_pages: data?.members_total_pages || 1,
      });
    } catch (err) {
      if (!silent) {
        setError(err.message || 'Failed to load members');
        setMembers([]);
      }
    } finally {
      if (silent) setMembersRefreshing(false);
      else setMembersLoading(false);
    }
  }, [memberPage, memberApiFilter, focusMember]);

  useEffect(() => { loadStats({ fresh: true }); }, [loadStats]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    const phone = params.get('phone');
    if (view === 'call-logs' || view === 'customer-call-logs') {
      setActiveView('customer');
      setCallLogsSearchTerm(phone || '');
      setSelectedBatchId(null);
      setShowCompleted(false);
      // Deep-linked from a customer's "View all calls" — search by phone across
      // all dates, not just today, otherwise a customer not called today appears empty.
      setCallLogsDatePreset('all');
      setCallLogsFromDate('');
      setCallLogsToDate('');
      window.history.replaceState({}, '', `${window.location.pathname}#/app/call-manager`);
    } else if (view === 'driver-call-logs' || view === 'driver-calls') {
      setActiveView('driver');
      setCallLogsSearchTerm(phone || '');
      setSelectedBatchId(null);
      setShowCompleted(false);
      setCallLogsDatePreset('all');
      setCallLogsFromDate('');
      setCallLogsToDate('');
      window.history.replaceState({}, '', `${window.location.pathname}#/app/call-manager`);
    } else if (view === 'unknown' || view === 'unknown-calls') {
      setActiveView('unknown');
      setCallLogsSearchTerm(phone || '');
      setSelectedBatchId(null);
      setShowCompleted(false);
      setCallLogsDatePreset('all');
      setCallLogsFromDate('');
      setCallLogsToDate('');
      window.history.replaceState({}, '', `${window.location.pathname}#/app/call-manager`);
    } else if (view === 'all' || view === 'all-calls') {
      setActiveView('all');
      setCallLogsSearchTerm(phone || '');
      setSelectedBatchId(null);
      setShowCompleted(false);
      setCallLogsDatePreset('all');
      setCallLogsFromDate('');
      setCallLogsToDate('');
      window.history.replaceState({}, '', `${window.location.pathname}#/app/call-manager`);
    }
  }, [location.search]);
  useEffect(() => {
    setBatchPage(1);
    setSelectedBatchId(null);
    setMembers([]);
    setSelectedBatchDescription('');
  }, [startDate, endDate]);
  useEffect(() => {
    if (!waStatsOpen) return undefined;
    const onDoc = (event) => {
      if (waStatsRef.current && !waStatsRef.current.contains(event.target)) {
        setWaStatsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [waStatsOpen]);
  useEffect(() => { loadBatches(batchPage); }, [batchPage, loadBatches]);
  useEffect(() => {
    if (selectedBatchId) refreshBatchMemberCounts(selectedBatchId);
  }, [selectedBatchId, refreshBatchMemberCounts]);
  useEffect(() => {
    if (selectedBatchId) {
      loadMembers(selectedBatchId, memberPage, { filter: memberApiFilter });
    }
  }, [selectedBatchId, memberPage, memberApiFilter, loadMembers]);

  useEffect(() => {
    if (!selectedBatchId || panelOpen) return undefined;
    const timer = window.setInterval(() => {
      loadMembers(selectedBatchId, memberPage, { silent: true, filter: memberApiFilter });
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [selectedBatchId, memberPage, memberApiFilter, panelOpen, loadMembers]);

  const refreshBatchesOnly = useCallback(() => {
    if (!selectedBatchId) {
      loadBatches(batchPage);
    } else {
      loadMembers(selectedBatchId, memberPage, { silent: true, filter: memberApiFilter });
    }
    setNextRefreshIn(POLLING_INTERVAL / 1000);
  }, [selectedBatchId, memberPage, memberApiFilter, loadMembers, loadBatches, batchPage]);

  const loadRefollowSummary = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefollowSummaryLoading(true);
    try {
      const data = await fetchRefollowStats();
      setRefollowStats({
        due_today: Number(data?.due_today) || 0,
        done_today: Number(data?.done_today) || 0,
        cancelled_today: Number(data?.cancelled_today) || 0,
        upcoming: Number(data?.upcoming) || 0,
        draft_batches: Number(data?.draft_batches) || 0,
        wa_sent: Number(data?.wa_sent) || 0,
        wa_delivered: Number(data?.wa_delivered) || 0,
        wa_read: Number(data?.wa_read) || 0,
        wa_replied: Number(data?.wa_replied) || 0,
        wa_failed: Number(data?.wa_failed) || 0,
      });
    } catch {
      if (!silent) {
        setRefollowStats({
          due_today: 0,
          done_today: 0,
          cancelled_today: 0,
          upcoming: 0,
          draft_batches: 0,
          wa_sent: 0,
          wa_delivered: 0,
          wa_read: 0,
          wa_replied: 0,
          wa_failed: 0,
        });
      }
    } finally {
      if (!silent) setRefollowSummaryLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    if (activeView === 'refollow') {
      loadRefollowSummary();
      setRefollowRefreshTick((t) => t + 1);
      setNextRefreshIn(POLLING_INTERVAL / 1000);
      return;
    }
    if (!selectedBatchId) {
      loadStats({ fresh: true });
      loadBatches(batchPage);
      loadRefollowSummary();
    } else {
      loadMembers(selectedBatchId, memberPage, { silent: true, filter: memberApiFilter });
    }
    setNextRefreshIn(POLLING_INTERVAL / 1000);
  }, [
    activeView,
    selectedBatchId,
    memberPage,
    memberApiFilter,
    loadMembers,
    loadBatches,
    batchPage,
    loadStats,
    loadRefollowSummary,
  ]);

  useEffect(() => {
    if (!focusMember || !selectedBatchId || membersLoading) return undefined;
    const last10 = focusMember.phoneLast10;
    const mid = focusMember.batchMemberId;
    const found = members.some((m) => (
      (mid && Number(m.id || m.batch_member_id) === Number(mid))
      || (last10 && phoneLast10(m.phone_e164 || m.phone) === last10)
    ));
    if (!found) {
      if (!focusMember.triedOtherTab) {
        setFocusMember((prev) => (prev ? { ...prev, triedOtherTab: true } : prev));
        setMemberTab((prev) => (prev === 'pending' ? 'actioned' : 'pending'));
        setMemberSubFilter('');
        setMemberPage(1);
      }
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const el = document.querySelector(
        last10 ? `[data-phone-last10="${last10}"]` : `[data-member-id="${mid}"]`,
      );
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [focusMember, selectedBatchId, members, membersLoading]);

  const refreshRefollowOnly = useCallback(() => {
    loadRefollowSummary({ silent: true });
    setRefollowRefreshTick((t) => t + 1);
    setNextRefreshIn(POLLING_INTERVAL / 1000);
  }, [loadRefollowSummary]);

  // Auto-refresh batches more often than the heavy stats strip (batches view only)
  useEffect(() => {
    if (activeView !== 'batches') return undefined;
    const batchInterval = setInterval(refreshBatchesOnly, POLLING_INTERVAL);
    const statsInterval = setInterval(() => {
      if (!selectedBatchId) {
        loadStats();
        loadRefollowSummary({ silent: true });
      }
    }, STATS_POLL_INTERVAL);
    return () => {
      clearInterval(batchInterval);
      clearInterval(statsInterval);
    };
  }, [refreshBatchesOnly, loadStats, loadRefollowSummary, activeView, selectedBatchId]);

  useEffect(() => {
    if (activeView !== 'refollow') return undefined;
    setNextRefreshIn(POLLING_INTERVAL / 1000);
    const interval = setInterval(refreshRefollowOnly, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [activeView, refreshRefollowOnly]);

  // Tick down countdown every second (batches + follow-up)
  useEffect(() => {
    if (activeView !== 'batches' && activeView !== 'refollow') return undefined;
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [activeView]);

  const switchToView = (view, phone = '', { refollowTab = 'due_today' } = {}) => {
    setActiveView(view);
    setCallLogsSearchTerm(phone || '');
    setSelectedBatchId(null);
    setMembers([]);
    setSelectedBatchDescription('');
    setRefollowSelectMode(false);
    setRefollowSelectedIds(new Set());
    setRefollowSelectedCache(new Map());
    setRefollowModalOpen(false);
    if (view === 'refollow') setRefollowQueueTab(refollowTab);
    closeCompletedModal();
    setShowRefollowCompleted(false);
  };

  const openRefollowQueue = () => {
    switchToView('refollow', '', { refollowTab: 'due_today' });
  };

  const toggleRefollowSelect = useCallback((memberRowId, member) => {
    if (!memberRowId) return;
    setRefollowSelectedIds((prev) => {
      const removing = prev.has(memberRowId);
      setRefollowSelectedCache((cache) => {
        const nextCache = new Map(cache);
        if (removing) nextCache.delete(memberRowId);
        else if (member) nextCache.set(memberRowId, member);
        return nextCache;
      });
      const next = new Set(prev);
      if (removing) next.delete(memberRowId);
      else next.add(memberRowId);
      return next;
    });
  }, []);

  const clearRefollowSelection = useCallback(() => {
    setRefollowSelectedIds(new Set());
    setRefollowSelectedCache(new Map());
  }, []);

  const mergeRefollowPageIntoSelection = useCallback((memberList, subFilter) => {
    const eligible = (memberList || []).filter((m) => memberMatchesSubFilterForRefollow(m, subFilter));
    setRefollowSelectedIds((prev) => {
      const next = new Set(prev);
      eligible.forEach((m) => {
        const id = m.id || m.batch_member_id;
        if (id) next.add(id);
      });
      return next;
    });
    setRefollowSelectedCache((prev) => {
      const next = new Map(prev);
      eligible.forEach((m) => {
        const id = m.id || m.batch_member_id;
        if (id) next.set(id, m);
      });
      return next;
    });
  }, []);

  const refollowPickerActiveInCompleted = Boolean(showCompleted && completedViewBatch);

  const refollowPickerMembers = refollowPickerActiveInCompleted ? completedMembers : members;

  const refollowMatchFilter = refollowMatchFilterKey({
    inCompleted: refollowPickerActiveInCompleted,
    completedOutcomeFilter,
    memberApiFilter,
  });

  const refollowPickerApiFilter = refollowApiFilterForPicker({
    inCompleted: refollowPickerActiveInCompleted,
    completedOutcomeFilter,
    memberApiFilter,
  });

  const refollowSourceBatchId = refollowPickerActiveInCompleted
    ? completedViewBatch?.id
    : selectedBatchId;

  const REFOLLOW_CHECK_ALL_PAGE_SIZE = 50;

  const selectAllRefollowOnPage = useCallback(() => {
    mergeRefollowPageIntoSelection(refollowPickerMembers, refollowMatchFilter);
  }, [refollowPickerMembers, refollowMatchFilter, mergeRefollowPageIntoSelection]);

  const selectAllRefollowEligible = useCallback(async () => {
    const batchId = refollowSourceBatchId;
    if (!batchId) return;
    setRefollowCheckAllBusy(true);
    try {
      const collected = [];
      let page = 1;
      let totalPages = 1;
      while (page <= totalPages) {
        const data = await fetchSfBatchDetail(batchId, {
          page,
          pageSize: REFOLLOW_CHECK_ALL_PAGE_SIZE,
          memberFilter: refollowPickerApiFilter,
        });
        const rows = attachBatchMemberIds(
          data?.member_live || data?.batch?.members || [],
          data?.batch?.members || [],
        );
        collected.push(...rows);
        totalPages = data?.members_total_pages || 1;
        page += 1;
      }
      const eligible = collected.filter((m) => memberMatchesSubFilterForRefollow(m, refollowMatchFilter));
      setRefollowSelectedIds((prev) => {
        const next = new Set(prev);
        eligible.forEach((m) => {
          const id = m.id || m.batch_member_id;
          if (id) next.add(id);
        });
        return next;
      });
      setRefollowSelectedCache((prev) => {
        const next = new Map(prev);
        eligible.forEach((m) => {
          const id = m.id || m.batch_member_id;
          if (id) next.set(id, m);
        });
        return next;
      });
    } catch (e) {
      showToast(e.message || 'Could not load all members', 'error');
    } finally {
      setRefollowCheckAllBusy(false);
    }
  }, [
    refollowSourceBatchId,
    refollowPickerApiFilter,
    refollowMatchFilter,
    showToast,
  ]);

  const refollowSelectedMembers = useMemo(() => {
    const list = [];
    refollowSelectedIds.forEach((id) => {
      const m = refollowSelectedCache.get(id);
      if (m && memberEligibleForRefollow(m)) list.push(m);
    });
    return list;
  }, [refollowSelectedIds, refollowSelectedCache]);

  const refollowSelectedCount = refollowSelectedMembers.length;

  const submitRefollowBatch = useCallback(async (payload) => {
    setRefollowSubmitting(true);
    try {
      await createRefollowBatch(payload);
      showToast(
        payload.publish ? 'Refollow-up batch created' : 'Refollow draft saved',
        'success',
      );
      setRefollowModalOpen(false);
      setRefollowSelectMode(false);
      clearRefollowSelection();
      await loadRefollowSummary();
      setRefollowRefreshTick((t) => t + 1);
      if (!payload.publish) {
        switchToView('refollow', '', { refollowTab: 'draft' });
      }
    } catch (e) {
      showToast(e.message || 'Could not create refollow batch', 'error');
    } finally {
      setRefollowSubmitting(false);
    }
  }, [loadRefollowSummary, clearRefollowSelection, switchToView]);

  const scheduleDeferredRefresh = useCallback((delayMs = 4000) => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      if (selectedBatchId) {
        loadMembers(selectedBatchId, memberPage, { silent: true, filter: memberApiFilter });
        refreshBatchMemberCounts(selectedBatchId);
      }
      loadStats({ fresh: true });
      refreshTimerRef.current = null;
    }, delayMs);
  }, [selectedBatchId, memberPage, memberApiFilter, loadMembers, loadStats, refreshBatchMemberCounts]);

  useEffect(() => () => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    if (recentActionTimerRef.current) window.clearTimeout(recentActionTimerRef.current);
  }, []);

  const showRecentAction = useCallback((member, patch) => {
    if (!patch?.outcome) return;
    const key = memberListKey(member);
    setRecentAction({
      key,
      memberId: member?.id,
      phone: member?.phone_e164 || member?.phone,
      memberName: member?.name || member?.greeting_name,
      outcome: patch.outcome,
      outcomeLabel: patch.outcome_label || patch.outcome.replace(/_/g, ' '),
    });
    if (recentActionTimerRef.current) window.clearTimeout(recentActionTimerRef.current);
    recentActionTimerRef.current = window.setTimeout(() => {
      setRecentAction(null);
      recentActionTimerRef.current = null;
    }, ACTION_BANNER_MS);
  }, []);

  const handleRowRefresh = useCallback((opts) => {
    if (opts?.soft) {
      const delayMs = opts?.delayMs ?? 400;
      if (selectedBatchId) {
        window.setTimeout(() => {
          loadMembers(selectedBatchId, memberPage, { silent: true, filter: memberApiFilter });
        }, delayMs);
      }
      window.setTimeout(() => {
        loadStats({ fresh: true });
      }, delayMs);
      return;
    }
    scheduleDeferredRefresh();
  }, [selectedBatchId, memberPage, memberApiFilter, loadMembers, loadStats, scheduleDeferredRefresh]);

  const openBatch = (batch) => {
    setActiveView('batches');
    setRefollowSelectMode(false);
    setRefollowSelectedIds(new Set());
    setRefollowSelectedCache(new Map());
    setRefollowModalOpen(false);
    setSelectedBatchId(batch.id);
    setSelectedBatchName(batch.name || `Batch #${batch.id}`);
    setSelectedBatchDescription(batch.description || '');
    setSelectedBatchCreatedAt(batch.created_at || '');
    setSelectedBatchSource(batch.completion_source || '');
    setSelectedBatchVoiceTarget(batch.voice_target_type || batchVoiceTargetType(batch));
    const prog = queueProgressFromBatch(batch);
    setSelectedBatchProgress(prog);
    const pendingN = Number(prog.pending) || 0;
    const actionedN = Number(prog.actioned) || 0;
    setMemberTab(pendingN > 0 ? 'pending' : (actionedN > 0 ? 'actioned' : 'pending'));
    setMemberSubFilter('');
    setMemberPage(1);
    setFocusMember(null);
  };

  const openStatsBatchRow = (row) => {
    if (!row?.batch_id) return;
    const tone = String(row.row_tone || row.action_label || row.outcome || '').toLowerCase();
    const stayPending = (
      tone.includes('follow')
      || tone.includes('not picked')
      || tone.includes('not_picked')
      || tone.includes('not_contacted')
      || tone.includes('pending')
    );
    const batch = batches.find((b) => b.id === row.batch_id);
    setStatsFilterBucket('');
    setStatsFilterData(null);
    setActiveView('batches');
    setSelectedBatchId(row.batch_id);
    if (batch) setSelectedBatchProgress(queueProgressFromBatch(batch));
    setSelectedBatchName(batch?.name || row.batch_label || `Batch #${row.batch_id}`);
    setSelectedBatchDescription(batch?.description || '');
    setSelectedBatchCreatedAt(batch?.created_at || '');
    setSelectedBatchSource(batch?.completion_source || '');
    setSelectedBatchVoiceTarget(batch?.voice_target_type || batchVoiceTargetType(batch || {}));
    setMemberTab(stayPending ? 'pending' : 'actioned');
    setMemberSubFilter('');
    setMemberPage(1);
    setFocusMember({
      batchMemberId: row.batch_member_id || null,
      phoneLast10: phoneLast10(row.phone_e164),
    });
  };

  const openPanel = (member) => {
    const ctx = memberRowToPanelContext({
      ...member,
      batch_id: member?.batch_id || selectedBatchId,
      batch_voice_target: member?.batch_voice_target || selectedBatchVoiceTarget,
      batch_completion_source: member?.batch_completion_source || selectedBatchSource,
    });
    const key = memberListKey(ctx);
    if (key) autoOpenedCustomerCreatedRef.current.add(key);
    setPanelMember(ctx);
    setPanelOpen(true);
    setPanelOpenKey(key);
    if (key && String(ctx?.outcome || '').toLowerCase() === 'customer_created') {
      setHoldCustomerCreatedKeys((prev) => new Set(prev).add(key));
    }
  };

  const openRefollowCustomer = useCallback((row, { stackAboveOverlay = false } = {}) => {
    if (stackAboveOverlay) setPanelStackAboveOverlay(true);
    openPanel({
      customer_id: row.customer_id,
      phone_e164: row.phone_e164,
      name: row.name,
      id: row.source_batch_member_id,
      batch_member_id: row.source_batch_member_id,
      batch_id: row.source_batch_id ?? row.ops_batch_id ?? selectedBatchId,
      outcome: 'follow_up',
      domain: row.domain,
      category: row.category,
      address: row.address,
      city: row.city,
      pincode: row.pincode,
    });
  }, [selectedBatchId]);

  const closePanel = () => {
    setPanelStackAboveOverlay(false);
    const key = panelMember ? memberListKey(panelMember) : null;
    const oc = String(panelMember?.outcome || '').toLowerCase();
    if (key) autoOpenedCustomerCreatedRef.current.add(key);
    setPanelOpen(false);
    setPanelMember(null);
    setPanelOpenKey(null);
    if (key) {
      setHoldCustomerCreatedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      if (oc === 'customer_created' && panelMember) {
        showRecentAction(panelMember, {
          outcome: 'customer_created',
          outcome_label: panelMember.outcome_label || 'Customer created',
        });
      }
    }
  };

  useEffect(() => {
    if (!selectedBatchId || membersLoading) return;
    const prev = prevOutcomesRef.current;
    if (!panelOpen) {
      let opened = false;
      for (const m of members) {
        const key = memberListKey(m);
        const prevOc = prev.get(key);
        if (!opened && shouldAutoOpenCustomerCreated(m, prevOc, { autoOpenedKeys: autoOpenedCustomerCreatedRef.current })) {
          autoOpenedCustomerCreatedRef.current.add(key);
          setHoldCustomerCreatedKeys((h) => new Set(h).add(key));
          openPanel(m);
          opened = true;
        }
      }
    }
    for (const m of members) {
      prev.set(memberListKey(m), m?.outcome);
    }
  }, [members, membersLoading, selectedBatchId, panelOpen]);

  const pendingTotal = selectedBatchProgress?.pending ?? 0;
  const actionedTotal = selectedBatchProgress?.actioned ?? 0;
  const batchProgressPct = selectedBatchProgress?.total
    ? Math.round((100 * (selectedBatchProgress.actioned || 0)) / selectedBatchProgress.total)
    : 0;

  /** Outcome/status is per batch member row — never match by phone (same number can be in many batches). */
  const memberPatchMatches = (m, memberId, batchId) => {
    if (memberId == null || m?.id == null) return false;
    if (Number(m.id) !== Number(memberId)) return false;
    if (batchId != null && m?.batch_id != null && Number(m.batch_id) !== Number(batchId)) {
      return false;
    }
    return true;
  };

  const applyMemberPatch = (memberId, patch, batchId = null) => {
    let updatedMember = null;
    const merge = (m) => {
      const next = { ...m, ...patch };
      updatedMember = next;
      return next;
    };
    setMembers((prev) => prev.map((m) => (memberPatchMatches(m, memberId, batchId) ? merge(m) : m)));
    setSearchResults((prev) => prev.map((item) => {
      const m = item.member;
      if (Number(m?.id) !== Number(memberId)) return item;
      if (batchId != null && item.batch?.id != null && Number(item.batch.id) !== Number(batchId)) {
        return item;
      }
      return { ...item, member: merge({ ...m }) };
    }));
    setPanelMember((prev) => {
      if (!prev || !memberPatchMatches(prev, memberId, batchId)) return prev;
      return { ...prev, ...patch };
    });
    return updatedMember;
  };

  const patchMemberOutcome = (memberId, patch, batchId = null, opts = {}) => {
    const updatedMember = applyMemberPatch(memberId, patch, batchId);
    const waOnly = patch && (patch.whatsapp_sent || patch.last_wa) && !patch.outcome;
    if (waOnly) return updatedMember;
    if (!patch?.outcome || !isTerminalOutcome(patch.outcome)) return updatedMember;

    const oc = String(patch.outcome).toLowerCase();
    const key = memberId != null ? memberListKey({ id: memberId }) : '';
    if (oc === 'customer_created' && opts.deferActioned && key) {
      setHoldCustomerCreatedKeys((prev) => new Set(prev).add(key));
    }

    const bannerMember = updatedMember || {
      id: memberId,
      batch_id: batchId,
      phone_e164: patch.phone_e164,
      phone: patch.phone,
      name: patch.name || patch.outcome_label,
    };
    showRecentAction(bannerMember, patch);

    if (oc === 'customer_created' && opts.deferActioned) {
      return updatedMember;
    }
    if (selectedBatchId) {
      refreshBatchMemberCounts(selectedBatchId);
      scheduleDeferredRefresh(800);
    }
    return updatedMember;
  };

  const selectedBatchForNw = useMemo(() => ({
    id: selectedBatchId,
    completion_source: selectedBatchSource,
    voice_target_type: selectedBatchVoiceTarget,
  }), [selectedBatchId, selectedBatchSource, selectedBatchVoiceTarget]);

  const applyCmQuickCreateSuccess = useCallback((member, batchId, data, extra = {}) => {
    const dialed = member.phone_e164 || member.phone || member.dialed_phone || '';
    const waE164 = data.phone_e164 || formatCapturedWhatsappE164(extra.captured_whatsapp_number);
    const nwLeadId = data.non_whatsapp_lead_id ?? member.non_whatsapp_lead_id ?? member.lead_id;
    patchMemberOutcome(
      member.id,
      {
        outcome: 'customer_created',
        outcome_label: 'Customer created',
        outcome_at: new Date().toISOString(),
        customer_id: data.customer_id,
        lead_id: data.lead_id ? Number(data.lead_id) : member.lead_id,
        is_lead_only: false,
        phone_e164: waE164 || member.phone_e164,
        phone: waE164 || member.phone,
        dialed_phone: data.non_whatsapp_phone || dialed,
        non_wa_phone: data.non_whatsapp_phone || dialed,
        non_whatsapp_lead_id: nwLeadId ? Number(nwLeadId) : null,
        ...extra,
      },
      batchId,
      { deferActioned: true },
    );
    openPanel(memberRowToPanelContext({
      ...member,
      customer_id: data.customer_id,
      lead_id: data.lead_id ? Number(data.lead_id) : member.lead_id,
      is_lead_only: false,
      phone_e164: waE164 || member.phone_e164,
      phone: waE164 || member.phone,
      dialed_phone: data.non_whatsapp_phone || dialed,
      non_wa_phone: data.non_whatsapp_phone || dialed,
      non_whatsapp_lead_id: nwLeadId ? Number(nwLeadId) : null,
      outcome: 'customer_created',
      outcome_label: 'Customer created',
      outcome_at: new Date().toISOString(),
      batch_id: batchId,
      batch_voice_target: selectedBatchVoiceTarget,
      batch_completion_source: selectedBatchSource,
      ...extra,
    }));
    showToast(`Customer #${data.customer_id} created — add remarks`, 'success');
  }, [
    patchMemberOutcome,
    selectedBatchVoiceTarget,
    selectedBatchSource,
    showToast,
  ]);

  const runCmNwQuickCreate = useCallback(async (member, batch, capturedWa) => {
    const batchId = member?.batch_id || batch?.id || selectedBatchId;
    if (!batchId) throw new Error('No batch selected');
    const nwId = resolveNonWhatsappLeadId(member, batch);
    const wa = normalizeCapturedWhatsapp(capturedWa);
    if (!nwId || !wa) throw new Error('Captured WhatsApp number and non-WA lead id required');
    const data = await convertNonWhatsappMemberFromLead(
      { ...member, batch_id: batchId, non_whatsapp_lead_id: nwId },
      {
        captured_whatsapp_number: wa,
        name: member.name,
        business_name: member.business_name,
        business_category: member.business_category || member.category,
        domain: member.domain,
        matched_category: member.matched_category,
        address: member.address,
        city: member.city,
        pincode: member.pincode,
      },
      {
        customerApiBase: config.customerApiBase,
        authToken: opsAuthToken(),
        batchId,
        marketingInitiated: false,
      },
    );
    applyCmQuickCreateSuccess(member, batchId, data, { captured_whatsapp_number: wa });
  }, [applyCmQuickCreateSuccess, selectedBatchId]);

  const quickCreateCustomer = useCallback(async (member, batchOverride = null) => {
    const batchId = member?.batch_id || batchOverride?.id || selectedBatchId;
    if (!batchId) throw new Error('No batch selected');
    const batch = batchOverride || selectedBatchForNw;
    if (isNonWhatsappMarketingContext(member, batch)) {
      const wa = normalizeCapturedWhatsapp(member?.captured_whatsapp_number);
      if (!wa) {
        setNwCaptureModal({ member: { ...member, batch_id: batchId }, batch });
        return;
      }
      await runCmNwQuickCreate(member, batch, wa);
      return;
    }
    const data = await convertMemberFromLead(
      { ...member, batch_id: batchId },
      {
        customerApiBase: config.customerApiBase,
        authToken: opsAuthToken(),
        batchId,
        marketingInitiated: false,
      },
    );
    applyCmQuickCreateSuccess(member, batchId, data);
  }, [applyCmQuickCreateSuccess, runCmNwQuickCreate, selectedBatchForNw, selectedBatchId]);

  const editRecentActionStatus = () => {
    if (!recentAction) return;
    const member = members.find((m) => memberListKey(m) === recentAction.key);
    if (member) {
      openPanel(member);
    } else {
      openPanel({
        id: recentAction.memberId,
        phone_e164: recentAction.phone,
        phone: recentAction.phone,
        name: recentAction.memberName,
        batch_id: selectedBatchId,
        outcome: recentAction.outcome,
        outcome_label: recentAction.outcomeLabel,
      });
    }
    setRecentAction(null);
  };

  const markBatchComplete = () => {
    if (!selectedBatchId || !selectedBatchProgress) return;
    setCompleteModalOpen(true);
  };

  const confirmBatchComplete = async () => {
    if (!selectedBatchId || !selectedBatchProgress || completeConfirming) return;
    setCompleteConfirming(true);
    try {
      await completeOpsBatch(selectedBatchId, { force: true });
      showToast('Batch marked completed');
      setCompleteModalOpen(false);
      setSelectedBatchId(null);
      setMembers([]);
      setSelectedBatchProgress(null);
      loadStats({ fresh: true });
      loadBatches(batchPage);
      fetchSfManualQueue({
        page: 1,
        pageSize: 1,
        completionSource: 'ops_queue',
        escalated: true,
        opsCompletedOnly: true,
      })
        .then((d) => setCompletedBatchMeta((m) => ({ ...m, total: d?.total || 0 })))
        .catch(() => {});
    } catch (err) {
      showToast(err.message || 'Complete failed', 'error');
    } finally {
      setCompleteConfirming(false);
    }
  };

  const deleteMemberRow = async (mem) => {
    const phone = mem?.phone_e164 || mem?.phone;
    const batchId = mem?.batch_id || selectedBatchId;
    if (!batchId || !phone) return;
    if (!window.confirm(`Soft-delete ${phone} from batch #${batchId}? They stay in the batch as deleted and can be reimported.`)) return;
    try {
      await deleteSegmentFlowBatchMember(batchId, phone);
      showToast('Member soft-deleted');
      loadMembers(selectedBatchId, memberPage, { filter: memberApiFilter });
      loadStats({ fresh: true });
    } catch (err) {
      showToast(err.message || 'Remove failed', 'error');
    }
  };

  const restoreMemberRow = async (mem) => {
    const phone = mem?.phone_e164 || mem?.phone;
    const batchId = mem?.batch_id || selectedBatchId;
    if (!batchId || !phone) return;
    try {
      const result = await restoreSegmentFlowBatchMember(batchId, phone);
      showToast(result?.restored_to_pending ? 'Member restored to pending' : 'Member restored with previous status');
      if (selectedBatchId === batchId) {
        loadMembers(batchId, memberPage, { filter: memberApiFilter });
      }
      loadStats({ fresh: true });
    } catch (err) {
      showToast(err.message || 'Restore failed', 'error');
    }
  };

  const activeBatches = batches;

  const loadCompletedBatches = useCallback(async (page = 1) => {
    try {
      const data = await fetchSfManualQueue({
        page,
        pageSize: COMPLETED_BATCH_PAGE_SIZE,
        completionSource: 'ops_queue',
        escalated: true,
        opsCompletedOnly: true,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const rows = data?.batches || [];
      setCompletedBatchesList(rows);
      setCompletedBatchMeta({ total: data?.total || 0, total_pages: data?.total_pages || 1 });
      setCompletedBatchPage(page);
      const prog = {};
      rows.forEach((b) => { if (b?.id) prog[b.id] = queueProgressFromBatch(b); });
      setProgress((prev) => ({ ...prev, ...prog }));
    } catch (err) {
      showToast(err.message || 'Failed to load completed batches', 'error');
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (activeView === 'batches' && !selectedBatchId) {
      loadRefollowSummary({ silent: false });
    }
  }, [activeView, selectedBatchId, loadRefollowSummary]);

  useEffect(() => {
    if (activeView === 'refollow') {
      loadRefollowSummary({ silent: true });
    }
  }, [activeView, loadRefollowSummary]);

  const closeCompletedModal = () => {
    setShowCompleted(false);
    setCompletedViewBatch(null);
    setCompletedMembers([]);
    setCompletedMembersPage(1);
    setCompletedViewProgress(null);
    setCompletedOutcomeFilter('all');
    setRefollowSelectMode(false);
    setRefollowSelectedIds(new Set());
    setRefollowModalOpen(false);
  };

  const closeRefollowCompletedModal = () => {
    setShowRefollowCompleted(false);
  };

  const loadCompletedBatchMembers = useCallback(async (batch, page, outcomeFilter) => {
    if (!batch?.id) return;
    setCompletedMembersLoading(true);
    try {
      setCompletedViewProgress(progress[batch.id] || queueProgressFromBatch(batch));

      const memberFilter = outcomeFilter && outcomeFilter !== 'all' ? outcomeFilter : null;
      const data = await fetchSfBatchDetail(batch.id, {
        page,
        pageSize: COMPLETED_MEMBER_PAGE_SIZE,
        memberFilter,
      });
      const batchVoiceTarget = data?.batch?.voice_target_type || batch?.voice_target_type || '';
      const batchCompletionSource = data?.batch?.completion_source || batch?.completion_source || '';
      const rows = attachBatchMemberIds(
        data?.member_live || data?.batch?.members || [],
        data?.batch?.members || [],
      );
      const pageRows = sortMembers(rows.map((m) => ({
        ...m,
        batch_id: batch.id,
        batch_voice_target: batchVoiceTarget,
        batch_completion_source: batchCompletionSource,
      })))
        .slice(0, COMPLETED_MEMBER_PAGE_SIZE);
      setCompletedMembers(pageRows);
      setCompletedMembersPage(page);
      setCompletedMemberMeta({
        total: data?.members_total || rows.length,
        total_pages: data?.members_total_pages || 1,
      });
    } catch (err) {
      showToast(err.message || 'Failed to load batch members', 'error');
      setCompletedMembers([]);
    } finally {
      setCompletedMembersLoading(false);
    }
  }, [progress]);

  const openCompletedBatch = async (batch, page = 1, outcomeFilter = 'all') => {
    const isNewBatch = batch.id !== completedViewBatch?.id;
    const filter = isNewBatch ? 'all' : outcomeFilter;
    if (isNewBatch) {
      setCompletedOutcomeFilter('all');
      setRefollowSelectMode(false);
      setRefollowSelectedIds(new Set());
      setRefollowModalOpen(false);
    } else {
      setCompletedOutcomeFilter(filter);
    }
    setCompletedViewBatch(batch);
    setCompletedMembersPage(page);
    await loadCompletedBatchMembers(batch, page, filter);
  };

  const reopenCompletedBatch = async () => {
    if (!completedViewBatch?.id || reopenConfirming) return;
    if (!window.confirm(
      `Mark batch #${completedViewBatch.id} incomplete?\n\nIt will move back to the active Call Manager batch list so ops can work it again.`,
    )) return;
    setReopenConfirming(true);
    try {
      await reopenOpsBatch(completedViewBatch.id);
      showToast('Batch marked incomplete — back in Call Manager batches');
      setCompletedViewBatch(null);
      setCompletedMembers([]);
      await loadCompletedBatches(completedBatchPage);
      loadBatches(batchPage);
      loadStats({ fresh: true });
    } catch (err) {
      showToast(err.message || 'Reopen failed', 'error');
    } finally {
      setReopenConfirming(false);
    }
  };

  return (
    <div className={s.root}>
      <div className={s.header}>
        <div>
          <h1 className={`${s.title}${activeView === 'refollow' ? ` ${s.titleFollowUp}` : ''}`}>
            {activeView === 'refollow' ? 'Follow up' : 'Call Manager'}
          </h1>
          <p className={s.subtitle}>
            {activeView === 'batches'
              ? `Ops-escalated batches — filtered by the header date range${
                startDate || endDate
                  ? ` (${startDate || '…'} → ${endDate || '…'})`
                  : ' (all dates)'
              }`
              : activeView === 'refollow'
                ? (
                  <>
                    <span className={s.refollowHeaderStat}>
                      <span aria-hidden>•</span>
                      {' '}
                      <strong>{refollowSummaryLoading ? '…' : refollowStats.due_today}</strong>
                      {' due today · '}
                      <span aria-hidden>•</span>
                      {' '}
                      <strong>{refollowSummaryLoading ? '…' : refollowStats.done_today}</strong>
                      {' done today'}
                    </span>
                  </>
                )
                : 'Call logs — search, filter, and review recordings'}
          </p>
        </div>
        <div className={s.headerActions}>
          <div className={s.headerToolbar}>
            {['customer', 'driver', 'lead', 'unknown', 'all'].includes(activeView) || cmBatchHomeSearchActive ? (
              <label className={`${s.memberSearchWrap} ${s.headerSearchBar}`}>
                <Search size={16} className={s.memberSearchIcon} aria-hidden />
                <input
                  type="search"
                  className={s.memberSearchInput}
                  placeholder={
                    ['customer', 'driver', 'lead', 'unknown', 'all'].includes(activeView)
                      ? 'Phone or name'
                      : 'Search active batch members'
                  }
                  value={
                    ['customer', 'driver', 'lead', 'unknown', 'all'].includes(activeView)
                      ? callLogsSearchTerm
                      : memberSearch
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (['customer', 'driver', 'lead', 'unknown', 'all'].includes(activeView)) {
                      setCallLogsSearchTerm(v);
                    } else {
                      setMemberSearch(v);
                    }
                  }}
                  aria-label={
                    ['customer', 'driver', 'lead', 'unknown', 'all'].includes(activeView)
                      ? 'Search call logs'
                      : 'Search members in active ops batches'
                  }
                />
              </label>
            ) : null}
            <div className={s.headerNavCluster}>
              <div className={s.headerViewTabs}>
                <button
                  type="button"
                  className={`${s.viewBtn} ${activeView === 'batches' ? s.viewBtnActive : ''}`}
                  onClick={() => switchToView('batches')}
                >
                  Ops Batches
                </button>
                <button
                  type="button"
                  className={`${s.viewBtn} ${activeView === 'refollow' ? s.viewBtnActive : ''}`}
                  onClick={() => switchToView('refollow', '', { refollowTab: 'due_today' })}
                >
                  Follow up
                </button>
                <button
                  type="button"
                  className={`${s.viewBtn} ${['customer', 'driver', 'lead', 'unknown', 'all'].includes(activeView) ? s.viewBtnActive : ''}`}
                  onClick={() => switchToView('customer')}
                >
                  Call Logs
                </button>
              </div>
              <span className={s.headerNavDivider} aria-hidden />
              <button
                type="button"
                className={`${s.viewBtn} ${s.viewBtnCompleted}${
                  activeView === 'refollow' && showRefollowCompleted ? ` ${s.viewBtnActive}` : ''
                }${showCompleted && activeView === 'batches' && !selectedBatchId ? ` ${s.viewBtnActive}` : ''}`}
                onClick={() => {
                  if (activeView === 'refollow') {
                    setShowCompleted(false);
                    setShowRefollowCompleted(true);
                    return;
                  }
                  setShowRefollowCompleted(false);
                  setShowCompleted(true);
                  loadCompletedBatches(1);
                }}
              >
                View completed
              </button>
              <button
                type="button"
                className={s.refreshBtn}
                onClick={refreshAll}
                disabled={activeView === 'refollow' ? refollowSummaryLoading : loading}
                title={`Auto-refreshing in ${Math.ceil(nextRefreshIn)}s`}
              >
                <i className={`fa ${(activeView === 'refollow' ? refollowSummaryLoading : loading) ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
                {!(activeView === 'refollow' ? refollowSummaryLoading : loading) && (
                  <div className={s.refreshProgressTrack}>
                    <div
                      className={s.refreshProgressBar}
                      style={{ width: `${Math.max(0, Math.min(100, (nextRefreshIn / (POLLING_INTERVAL / 1000)) * 100))}%` }}
                    />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeView === 'batches' && !selectedBatchId && (
        <>
          {statsError && !statsLoading ? (
            <div className={s.statsErrorBanner} role="alert">
              Stats could not load: {statsError}
              {' '}
              <button type="button" className={s.statsErrorRetry} onClick={() => loadStats({ fresh: true })}>Retry</button>
            </div>
          ) : null}
          <div className={s.statsStrip}>
            <div className={s.statsRow}>
              <div className={s.stat}><span>Active batches</span><strong>{statsLoading && !stats ? '…' : (stats?.active_batches ?? '—')}</strong></div>
              <div className={s.stat} title="Pending / unactioned members on open escalated batches">
                <span>Open members</span>
                <strong>{statsLoading && !stats ? '…' : (stats?.pending_members ?? '—')}</strong>
              </div>
              {[
                ['total', 'Total', stats?.actions_total],
                ['good_response', 'Good response', stats?.good_response],
                ['customers_created', 'Customers', stats?.customers_created],
                ['follow_ups', 'Follow ups', stats?.follow_ups],
                ['leads', 'Leads', stats?.leads],
                ['not_picked_up', 'Not picked', stats?.not_picked_up],
                ['dropped', 'Dropped', stats?.dropped],
                ['opted_out', 'Opted out', stats?.opted_out],
              ].map(([bucket, label, count]) => (
                <button
                  key={bucket}
                  type="button"
                  disabled={!stats || statsLoading}
                  className={`${s.stat} ${s.statClickable}${statsFilterBucket === bucket ? ` ${s.statActive}` : ''}`}
                  onClick={() => toggleStatsFilter(bucket)}
                  title="Ops ticks only — last action in selected date range (or today)"
                >
                  <span>{label}</span>
                  <strong>{statsLoading && !stats ? '…' : (count ?? 0)}</strong>
                </button>
              ))}
            </div>
            <div className={s.waStatWrap} ref={waStatsRef}>
              <button
                type="button"
                disabled={!stats || statsLoading}
                className={`${s.waStatBtn}${waStatsOpen || statsFilterBucket === 'wa_sent' ? ` ${s.waStatBtnOpen}` : ''}${statsFilterBucket === 'wa_sent' ? ` ${s.statActive}` : ''}`}
                onClick={() => setWaStatsOpen((open) => !open)}
                onDoubleClick={() => stats ? toggleStatsFilter('wa_sent') : undefined}
                title={
                  stats?.wa_error
                    ? 'WhatsApp counts timed out — click Retry on stats bar or refresh'
                    : 'Click for menu · double-click Sent total to filter list'
                }
                aria-expanded={waStatsOpen}
              >
                <MessageCircle size={14} aria-hidden />
                <span>WA sent</span>
                <strong>
                  {statsLoading && !stats
                    ? '…'
                    : (stats?.wa_loading
                      ? '…'
                      : (stats?.wa_error ? '—' : (stats?.wa_sent ?? 0)))}
                </strong>
                <ChevronDown size={14} aria-hidden />
              </button>
              {waStatsOpen && stats && (
                <div className={s.waStatMenu} role="menu">
                  {[
                    ['wa_sent', 'Sent', stats.wa_sent],
                    ['wa_delivered', 'Delivered', stats.wa_delivered],
                    ['wa_read', 'Read', stats.wa_read],
                    ['wa_replied', 'Replied', stats.wa_replied],
                    ['wa_failed', 'Failed', stats.wa_failed],
                  ].map(([bucket, label, count]) => (
                    <button
                      key={bucket}
                      type="button"
                      className={`${s.waStatMenuRow}${bucket === 'wa_failed' ? ` ${s.waStatFailed}` : ''}${statsFilterBucket === bucket ? ` ${s.waStatMenuRowActive}` : ''}`}
                      onClick={() => toggleStatsFilter(bucket)}
                    >
                      <span>{label}</span>
                      <strong>{count ?? 0}</strong>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {stats && statsFilterBucket && (
            <section className={s.statsFilterPanel} aria-live="polite">
              <div className={s.statsFilterHead}>
                <h3>{STATS_FILTER_LABELS[statsFilterBucket] || statsFilterBucket}</h3>
                <span className={s.statsFilterMeta}>
                  {statsStart === statsEnd
                    ? formatStatsDisplayDate(statsStart)
                    : `${formatStatsDisplayDate(statsStart)} – ${formatStatsDisplayDate(statsEnd)}`}
                  {' · '}
                  {String(statsFilterBucket).startsWith('wa_')
                    ? 'One row per customer · latest ops WhatsApp (cm_*) on escalated batches'
                    : 'One row per customer · last ops tick in range (marketing ticks excluded)'}
                </span>
                <button type="button" className={s.statsFilterClose} onClick={() => toggleStatsFilter(statsFilterBucket)}>
                  Close
                </button>
              </div>
              {statsFilterLoading ? (
                <div className={s.loading}>Loading…</div>
              ) : (
                <>
                  <div className={s.statsFilterTableWrap}>
                    <table className={s.statsFilterTable}>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Last action</th>
                          <th>WhatsApp</th>
                          <th>Batch</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(statsFilterData?.items || []).length === 0 ? (
                          <tr>
                            <td colSpan={7} className={s.statsFilterEmpty}>No rows for this filter.</td>
                          </tr>
                        ) : (statsFilterData?.items || []).map((row) => (
                          <tr
                            key={`${row.phone_e164}-${row.log_id || row.batch_member_id || row.action_at_ist}`}
                            className={s[statsFilterRowClass(row)]}
                          >
                            <td>{row.entity_id || '—'}</td>
                            <td>{row.name || '—'}</td>
                            <td>{row.phone_e164 || '—'}</td>
                            <td>{row.action_display || row.action_at_ist || '—'}</td>
                            <td>{row.wa_display || row.wa_status || 'Pending'}</td>
                            <td>
                              {row.batch_id ? (
                                <button
                                  type="button"
                                  className={s.statsFilterBatchLink}
                                  onClick={() => openStatsBatchRow(row)}
                                >
                                  {row.batch_label || `#${row.batch_id}`}
                                </button>
                              ) : (row.batch_label || '—')}
                            </td>
                            <td>{row.ops_user_role || 'ops'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {statsFilterData && statsFilterData.total_pages > 1 && (
                    <div className={s.statsFilterPager}>
                      <button
                        type="button"
                        disabled={statsFilterPage <= 1}
                        onClick={() => setStatsFilterPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </button>
                      <span>
                        Page {statsFilterPage} of {statsFilterData.total_pages}
                        {' '}
                        ({statsFilterData.total} total)
                      </span>
                      <button
                        type="button"
                        disabled={statsFilterPage >= statsFilterData.total_pages}
                        onClick={() => setStatsFilterPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          )}
        </>
      )}

      <div className={s.content}>
        {toast && (
          <div className={toast.type === 'error' ? s.toastError : s.toastSuccess}>{toast.msg}</div>
        )}
        {error && !toast && <div className={s.error}>{error}</div>}

        {activeView === 'batches' && !selectedBatchId && memberSearch.trim().length >= 2 && (
          <section className={s.searchResultsSection} aria-live="polite">
            {searchLoading ? (
              <div className={s.loading}>Searching members…</div>
            ) : searchResults.length === 0 ? (
              <div className={s.empty}>No matches in active batches for “{memberSearch.trim()}”.</div>
            ) : (
              <div className={s.searchHitList}>
                {searchResults.map(({ batch, member }) => (
                  <article key={`${batch.id}-${member.id || member.phone_e164}`} className={s.searchHitCard}>
                    <div className={s.searchHitBatch}>
                      <BatchSummaryBlock
                        compact
                        batchId={batch.id}
                        name={batch.name}
                        description={batch.description}
                        total={0}
                        actioned={0}
                        createdAt={batch.escalated_at}
                      />
                      <button
                        type="button"
                        className={s.searchOpenBatch}
                        onClick={() => openBatch({
                          ...batch,
                          created_at: batch.escalated_at || batch.created_at,
                        })}
                      >
                        Open batch
                      </button>
                    </div>
                    <CallManagerMemberRow
                      member={{ ...member, batch_id: batch.id }}
                      batchId={batch.id}
                      batch={batch}
                      isActioned={false}
                      recentActionKey={null}
                      onOpenPanel={(m) => openPanel({
                        ...m,
                        batch_id: batch.id,
                        batch_voice_target: batchVoiceTargetType(batch),
                        batch_completion_source: batch.completion_source,
                      })}
                      onQuickCreateCustomer={(m) => quickCreateCustomer(m, batch)}
                      onRefresh={() => searchCallManagerMembers(memberSearch.trim()).then((d) => setSearchResults(d?.items || []))}
                      onOutcomeChange={(id, patch, bid) => patchMemberOutcome(id, patch, bid)}
                      onDelete={(m) => deleteMemberRow({ ...m, batch_id: batch.id })}
                      onRestore={(m) => restoreMemberRow({ ...m, batch_id: batch.id })}
                      showToast={showToast}
                    />
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {activeView === 'batches' && !selectedBatchId && memberSearch.trim().length < 2 && (
          <>
            <div className={s.batchList}>
              <RefollowQueueCard
                stats={refollowStats}
                loading={refollowSummaryLoading}
                onClick={openRefollowQueue}
              />
              {loading ? (
                <div className={s.loading}>Loading batches…</div>
              ) : activeBatches.length === 0 ? (
                <div className={s.empty}>
                  No active ops-escalated batches
                  {startDate || endDate ? ' in the selected date range' : ''}
                  {batchMeta.total > 0 && (startDate || endDate)
                    ? ` (${batchMeta.total} total with filter — try clearing dates)`
                    : completedBatchMeta.total ? ' — open View Completed for closed batches.' : '.'}
                  {(startDate || endDate) ? (
                    <div className={s.emptyActions}>
                      <button type="button" className={s.secondaryBtn} onClick={clearDateFilter}>Show all dates</button>
                      <button type="button" className={s.secondaryBtn} onClick={setToday}>Today only</button>
                    </div>
                  ) : null}
                  {completedBatchMeta.total > 0 && !startDate && !endDate ? (
                    <p className={s.emptyHint}>No open batches — use View Completed for closed work.</p>
                  ) : null}
                </div>
              ) : (
                activeBatches.map((b) => {
                    const p = progress[b.id] || queueProgressFromBatch(b);
                    const total = p.total || b.progress_total || b.member_count || 0;
                    const actioned = p.actioned ?? b.actioned ?? 0;
                    const pct = total ? Math.round((100 * actioned) / total) : 0;
                    const isBot = String(b.completion_source || '').toLowerCase() === 'marketing_bot';
                    const isNwBatch = batchVoiceTargetType(b) === 'not_on_whatsapp';
                    return (
                      <button key={b.id} type="button" className={s.batchCard} onClick={() => openBatch(b)}>
                        <div className={s.batchCardInner}>
                          {isNwBatch ? (
                            <span
                              className={`${s.batchNwBadge}${isBot ? ` ${s.batchNwBadgeStacked}` : ''}`}
                              title="Non-WhatsApp dialed numbers — create customer on captured WhatsApp"
                            >
                              Non WhatsApp
                            </span>
                          ) : null}
                          {isBot ? (
                            <span className={s.batchBotBadge} title="Escalated from Marketing bot">
                              <i className="fa fa-android" aria-hidden />
                              <span>Bot</span>
                            </span>
                          ) : null}
                          <i className={`fa fa-chevron-right ${s.batchChev}`} aria-hidden />
                          <BatchSummaryBlock
                            batchId={b.id}
                            name={b.name}
                            description={b.description}
                            total={total}
                            actioned={actioned}
                            createdAt={b.created_at}
                          />
                        </div>
                        <div className={s.batchProgressTrack} aria-hidden>
                          <div className={s.batchProgressTrackFill} style={{ width: `${pct}%` }} />
                        </div>
                      </button>
                    );
                  })
              )}
            </div>
            {!loading && batchMeta.total_pages > 1 ? (
              <div className={s.pagination}>
                <button type="button" className={s.pagerBtn} disabled={batchPage <= 1} onClick={() => setBatchPage((p) => p - 1)}>Prev</button>
                <span>Page {batchPage} of {batchMeta.total_pages}</span>
                <button type="button" className={s.pagerBtn} disabled={batchPage >= batchMeta.total_pages} onClick={() => setBatchPage((p) => p + 1)}>Next</button>
              </div>
            ) : null}
          </>
        )}

        <div className={s.refollowViewPane} hidden={activeView !== 'refollow'} aria-hidden={activeView !== 'refollow'}>
          <div className={s.refollowBackRow}>
            <button
              type="button"
              className={s.backBtnCompact}
              onClick={() => switchToView('batches')}
            >
              ← Back to batches
            </button>
          </div>
          <RefollowUpSection
            queueTab={refollowQueueTab}
            onQueueTabChange={setRefollowQueueTab}
            refreshTick={refollowRefreshTick}
            queueStats={refollowStats}
            queueStatsLoading={refollowSummaryLoading}
            showToast={showToast}
            onOpenCustomer={openRefollowCustomer}
            onStatsRefresh={() => loadRefollowSummary({ silent: true })}
          />
        </div>

        {['customer', 'driver', 'lead', 'unknown', 'all'].includes(activeView) && (
          <div className={s.callLogsShell}>
            <div className={s.callLogsNav}>
              <div className={s.callLogsTabs}>
                <button
                  type="button"
                  className={`${s.callLogsTabBtn} ${activeView === 'customer' ? s.callLogsTabBtnActive : ''}`}
                  onClick={() => switchToView('customer', callLogsSearchTerm)}
                >
                  Customer Calls
                </button>
                <button
                  type="button"
                  className={`${s.callLogsTabBtn} ${activeView === 'driver' ? s.callLogsTabBtnActive : ''}`}
                  onClick={() => switchToView('driver', callLogsSearchTerm)}
                >
                  Driver & Transporter
                </button>
                <button
                  type="button"
                  className={`${s.callLogsTabBtn} ${activeView === 'lead' ? s.callLogsTabBtnActive : ''}`}
                  onClick={() => switchToView('lead', callLogsSearchTerm)}
                >
                  Marketing Calls
                </button>
                <button
                  type="button"
                  className={`${s.callLogsTabBtn} ${activeView === 'unknown' ? s.callLogsTabBtnActive : ''}`}
                  onClick={() => switchToView('unknown', callLogsSearchTerm)}
                >
                  Unknown Calls
                </button>
                <button
                  type="button"
                  className={`${s.callLogsTabBtn} ${activeView === 'all' ? s.callLogsTabBtnActive : ''}`}
                  onClick={() => switchToView('all', callLogsSearchTerm)}
                >
                  All Calls
                </button>
              </div>

              <div className={s.callLogsDateFilterRow}>
                <div className={s.datePresetGroup}>
                  <button
                    type="button"
                    className={`${s.datePresetBtn} ${callLogsDatePreset === 'today' ? s.datePresetBtnActive : ''}`}
                    onClick={() => handleSetCallLogsPreset('today')}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    className={`${s.datePresetBtn} ${callLogsDatePreset === 'yesterday' ? s.datePresetBtnActive : ''}`}
                    onClick={() => handleSetCallLogsPreset('yesterday')}
                  >
                    Yesterday
                  </button>
                  <button
                    type="button"
                    className={`${s.datePresetBtn} ${callLogsDatePreset === 'this_week' ? s.datePresetBtnActive : ''}`}
                    onClick={() => handleSetCallLogsPreset('this_week')}
                  >
                    This Week
                  </button>
                  <button
                    type="button"
                    className={`${s.datePresetBtn} ${callLogsDatePreset === 'custom' ? s.datePresetBtnActive : ''}`}
                    onClick={() => handleSetCallLogsPreset('custom')}
                  >
                    <i className="fa fa-calendar" style={{ marginRight: 4 }} />
                    Custom
                  </button>
                  <button
                    type="button"
                    className={`${s.datePresetBtn} ${callLogsDatePreset === 'all' ? s.datePresetBtnActive : ''}`}
                    onClick={() => handleSetCallLogsPreset('all')}
                  >
                    All Dates
                  </button>
                </div>

                {callLogsDatePreset === 'custom' && (
                  <div className={s.customDateRangeBox}>
                    <input
                      type="date"
                      value={callLogsFromDate}
                      onChange={(e) => handleCustomDateChange(e.target.value, callLogsToDate)}
                      className={s.customDateInput}
                      title="From Date"
                    />
                    <span className={s.dateSep}>→</span>
                    <input
                      type="date"
                      value={callLogsToDate}
                      onChange={(e) => handleCustomDateChange(callLogsFromDate, e.target.value)}
                      className={s.customDateInput}
                      title="To Date"
                    />
                  </div>
                )}
              </div>
            </div>

            <CallLogsAnalytics
              callerType={activeView}
              fromDate={callLogsFromDate}
              toDate={callLogsToDate}
            />

            {activeView === 'customer' ? (
              <CustomerCallLogs
                embedded
                initialSearchTerm={callLogsSearchTerm}
                fromDate={callLogsFromDate}
                toDate={callLogsToDate}
                onDateChange={handleCustomDateChange}
              />
            ) : (
              <DriverCallLogs
                embedded
                initialCallerType={activeView}
                onCallerTypeChange={(type) => setActiveView(type)}
                fromDate={callLogsFromDate}
                toDate={callLogsToDate}
                onDateChange={handleCustomDateChange}
              />
            )}
          </div>
        )}

        {activeView === 'batches' && selectedBatchId && (
          <>
            <div className={`${s.batchDetailHeader} ${s.batchDetailChrome}`}>
              <button
                type="button"
                className={s.backBtnCompact}
                onClick={() => {
                  setSelectedBatchId(null);
                  setMembers([]);
                  setSelectedBatchDescription('');
                  setSelectedBatchCreatedAt('');
                  setSelectedBatchSource('');
                  setFocusMember(null);
                  setRefollowSelectMode(false);
                  setRefollowSelectedIds(new Set());
                  setRefollowModalOpen(false);
                }}
              >
                ← Back to batches
              </button>
              <div className={`${s.batchInlineSummary}${membersRefreshing ? ` ${s.batchInlineSummaryRefreshing}` : ''}`}>
                <div className={s.batchInlineHead}>
                  <BatchSummaryBlock
                    compact
                    batchId={selectedBatchId}
                    name={selectedBatchName}
                    description={selectedBatchDescription}
                    total={selectedBatchProgress?.total || 0}
                    actioned={selectedBatchProgress?.actioned || 0}
                    createdAt={selectedBatchCreatedAt}
                  />
                  {batchVoiceTargetType(selectedBatchForNw) === 'not_on_whatsapp' ? (
                    <span className={`${s.batchNwBadge} ${s.batchNwBadgeInline}`} title="Non-WhatsApp import batch">
                      Non WhatsApp
                    </span>
                  ) : null}
                  {String(selectedBatchSource || '').toLowerCase() === 'marketing_bot' ? (
                    <span className={`${s.batchBotBadge} ${s.batchBotBadgeInline}`} title="Escalated from Marketing bot">
                      <i className="fa fa-android" aria-hidden />
                      <span>Bot</span>
                    </span>
                  ) : null}
                  {selectedBatchProgress && !selectedBatchProgress.completed && (
                    selectedBatchProgress.can_manual_complete
                    || (
                      (selectedBatchProgress.pending ?? 0) === 0
                      && (selectedBatchProgress.untouched_count ?? 0) === 0
                      && (selectedBatchProgress.total ?? 0) > 0
                    )
                  ) ? (
                    <button type="button" className={s.completeBatchBtnInline} onClick={markBatchComplete}>
                      Mark completed
                    </button>
                  ) : null}
                </div>
                <div className={s.batchProgressTrack} aria-hidden>
                  <div className={s.batchProgressTrackFill} style={{ width: `${batchProgressPct}%` }} />
                </div>
              </div>
            </div>

            <ActionBannerDock action={recentAction}>
              <ActionConfirmationBanner
                action={recentAction}
                onEdit={editRecentActionStatus}
              />
            </ActionBannerDock>

            {membersLoading && members.length === 0 ? (
              <div className={s.loading}>Loading members…</div>
            ) : (
              <>
                <div className={`${s.memberFilterBar} ${s.batchDetailChrome}`}>
                  <div className={s.memberFilterBarMain}>
                    <div className={s.memberFilterTabs} role="tablist" aria-label="Member queue">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={memberTab === 'pending'}
                        className={`${s.memberFilterTab}${memberTab === 'pending' ? ` ${s.memberFilterTabActive}` : ''}`}
                        onClick={() => {
                          setMembers([]);
                          setMemberTab('pending');
                          setMemberSubFilter('');
                          setMemberPage(1);
                        }}
                      >
                        Pending <span className={s.memberFilterCount}>{pendingTotal}</span>
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={memberTab === 'actioned'}
                        className={`${s.memberFilterTab}${memberTab === 'actioned' ? ` ${s.memberFilterTabActive}` : ''}`}
                        onClick={() => {
                          setMembers([]);
                          setMemberTab('actioned');
                          setMemberSubFilter('');
                          setMemberPage(1);
                        }}
                      >
                        Actioned <span className={s.memberFilterCount}>{actionedTotal}</span>
                      </button>
                    </div>
                    <div
                      className={s.memberSubFilterTabs}
                      role="tablist"
                      aria-label={memberTab === 'pending' ? 'Pending outcome filter' : 'Actioned outcome filter'}
                    >
                      {(memberTab === 'pending' ? PENDING_MEMBER_SUB_FILTERS : ACTIONED_MEMBER_SUB_FILTERS).map(
                        ({ key, label }) => {
                          const active = memberSubFilter === key;
                          const count = outcomeCountFromProgress(selectedBatchProgress, key);
                          return (
                            <button
                              key={key}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              className={`${s.memberSubFilterTab}${active ? ` ${s.memberSubFilterTabActive}` : ''}`}
                              onClick={() => {
                                setMembers([]);
                                setMemberSubFilter((prev) => (prev === key ? '' : key));
                                setMemberPage(1);
                              }}
                            >
                              {label}
                              <span className={s.memberSubFilterCount}>{count}</span>
                            </button>
                          );
                        },
                      )}
                    </div>
                    <div className={s.memberFilterBarActions}>
                      <RefollowSelectToolbar
                        selectMode={refollowSelectMode}
                        selectedCount={refollowSelectedCount}
                        checkAllBusy={refollowCheckAllBusy}
                        onStart={() => {
                          setRefollowSelectMode(true);
                          clearRefollowSelection();
                        }}
                        onCheckAll={selectAllRefollowEligible}
                        onCheckPage={selectAllRefollowOnPage}
                        onCreate={() => setRefollowModalOpen(true)}
                        onCancel={() => {
                          setRefollowSelectMode(false);
                          clearRefollowSelection();
                        }}
                      />
                    </div>
                  </div>
                  <span className={s.memberFilterHint}>
                    {memberTab === 'pending' ? 'Who to call next' : 'Completed — click Edit on any row to correct'}
                    {memberSubFilter ? ` · Filter: ${(memberTab === 'pending' ? PENDING_MEMBER_SUB_FILTERS : ACTIONED_MEMBER_SUB_FILTERS).find((f) => f.key === memberSubFilter)?.label || memberSubFilter}` : ''}
                    {membersRefreshing ? ' · Syncing…' : ''}
                  </span>
                </div>

                <div className={s.batchMembersBody}>
                <div
                  className={`${s.memberListWrap}${membersLoading || membersRefreshing ? ` ${s.memberListWrapBusy}` : ''}`}
                  key={`${memberApiFilter}-${memberPage}`}
                >
                  {members.length > 0 ? (
                    <CallManagerMemberList
                      members={members}
                      batchId={selectedBatchId}
                      batch={selectedBatchForNw}
                      isActionedView={memberListActioned}
                      focusMember={focusMember}
                      recentActionKey={recentAction?.key}
                      onOpenPanel={openPanel}
                      onQuickCreateCustomer={quickCreateCustomer}
                      onRefresh={handleRowRefresh}
                      onOutcomeChange={patchMemberOutcome}
                      onDelete={deleteMemberRow}
                      onRestore={restoreMemberRow}
                      showToast={showToast}
                      refollowSelectMode={refollowSelectMode}
                      refollowSelectedIds={refollowSelectedIds}
                      onToggleRefollowSelect={toggleRefollowSelect}
                    />
                  ) : (
                    <div className={s.empty}>
                      No members match this filter
                      {memberMeta.total_pages > 1 ? ' on this page' : ''}.
                    </div>
                  )}
                </div>

                {memberMeta.total_pages > 1 && (
                  <div className={s.pagination}>
                    <button type="button" className={s.pagerBtn} disabled={memberPage <= 1} onClick={() => setMemberPage((p) => p - 1)}>Prev</button>
                    <span>Page {memberPage} of {memberMeta.total_pages}</span>
                    <button type="button" className={s.pagerBtn} disabled={memberPage >= memberMeta.total_pages} onClick={() => setMemberPage((p) => p + 1)}>Next</button>
                  </div>
                )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <CallManagerMemberPanel
        isOpen={panelOpen}
        onClose={closePanel}
        member={panelMember}
        stackAboveOverlay={panelStackAboveOverlay}
        customerApiBase={config.customerApiBase}
        authToken={opsAuthToken()}
        batchId={panelMember?.batch_id}
        activityRefreshKey={panelActivityKey}
        showToast={showToast}
        onViewAllCalls={(phone) => {
          closePanel();
          setActiveView('customer');
          setCallLogsSearchTerm(phone || '');
        }}
        onActionRecorded={async (patch) => {
          if (panelMember?.id && patch?.deferActioned) {
            patchMemberOutcome(
              panelMember.id,
              {
                outcome: patch.outcome || 'customer_created',
                outcome_label: patch.outcome_label || 'Customer created',
                outcome_at: patch.outcome_at || new Date().toISOString(),
                customer_id: patch.customer_id,
                lead_id: patch.lead_id,
                is_lead_only: false,
                is_non_whatsapp_lead: false,
                non_whatsapp_lead_id: null,
              },
              panelMember.batch_id || selectedBatchId,
              { deferActioned: true },
            );
            setPanelActivityKey((k) => k + 1);
          } else if (panelMember?.id && patch?.outcome && !patch?.skipOutcomePatch) {
            patchMemberOutcome(
              panelMember.id,
              patch,
              panelMember.batch_id || selectedBatchId,
            );
            setPanelActivityKey((k) => k + 1);
          } else if (panelMember?.id && patch?.customer_id) {
            applyMemberPatch(
              panelMember.id,
              { ...patch, is_lead_only: false },
              panelMember.batch_id || selectedBatchId,
            );
          }
          if (selectedBatchId && !patch?.deferActioned) {
            try {
              const prog = await fetchBatchActionProgress(selectedBatchId);
              setSelectedBatchProgress(prog);
              if (prog?.completed) {
                showToast('Batch completed — moved to View Completed');
                window.setTimeout(() => {
                  setSelectedBatchId(null);
                  setMembers([]);
                  loadBatches(batchPage);
                }, 1200);
              }
            } catch {
              /* non-fatal */
            }
          }
        }}
        onConverted={({ customer_id: newCustomerId, lead_id: newLeadId } = {}) => {
          if (newCustomerId && panelMember) {
            const key = memberListKey(panelMember);
            if (key) autoOpenedCustomerCreatedRef.current.add(key);
            applyMemberPatch(
              panelMember.id,
              {
                customer_id: newCustomerId,
                lead_id: newLeadId != null ? Number(newLeadId) : panelMember.lead_id,
                is_lead_only: false,
                is_non_whatsapp_lead: false,
                non_whatsapp_lead_id: null,
                outcome: panelMember.outcome || 'customer_created',
                outcome_label: panelMember.outcome_label || 'Customer created',
                outcome_at: panelMember.outcome_at || new Date().toISOString(),
              },
              panelMember.batch_id || selectedBatchId,
            );
            setPanelActivityKey((k) => k + 1);
          }
        }}
        onRemarksSaved={() => scheduleDeferredRefresh()}
      />

      <RefollowCompletedPanel
        open={showRefollowCompleted}
        onClose={closeRefollowCompletedModal}
        showToast={showToast}
        onOpenCustomer={(row) => openRefollowCustomer(row, { stackAboveOverlay: true })}
        refreshTick={refollowRefreshTick}
      />

      {showCompleted ? (
        <div className={s.modalBackdrop} onClick={closeCompletedModal}>
          <div
            className={`${s.completedModal}${completedViewBatch ? ` ${s.completedModalWide}` : ''}`}
            role="dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.completedModalHead}>
              <div>
                <h2>{completedViewBatch ? `#${completedViewBatch.id} ${completedViewBatch.name || 'Batch'}` : 'Completed batches'}</h2>
                {completedViewBatch ? (
                  <p className={s.completedSubhead}>
                    Read-only audit — filter by outcome, {COMPLETED_MEMBER_PAGE_SIZE} members per page
                  </p>
                ) : null}
              </div>
              <div className={s.completedModalActions}>
                {completedViewBatch ? (
                  <>
                    <button
                      type="button"
                      className={s.completedBack}
                      onClick={() => {
                        setCompletedViewBatch(null);
                        setCompletedMembers([]);
                        setCompletedMembersPage(1);
                        setCompletedViewProgress(null);
                        setCompletedOutcomeFilter('all');
                      }}
                    >
                      Back to list
                    </button>
                    <button
                      type="button"
                      className={s.completedReopen}
                      disabled={reopenConfirming}
                      onClick={reopenCompletedBatch}
                    >
                      {reopenConfirming ? 'Updating…' : 'Mark incomplete'}
                    </button>
                  </>
                ) : null}
                <button type="button" className={s.completedClose} onClick={closeCompletedModal}>Close</button>
              </div>
            </div>
            {!completedViewBatch ? (
              <>
                <p className={s.completedHint}>
                  {completedBatchMeta.total} completed batch{completedBatchMeta.total === 1 ? '' : 'es'}
                  {' · '}
                  {COMPLETED_BATCH_PAGE_SIZE} batches per page — open a batch for {COMPLETED_MEMBER_PAGE_SIZE} members per page.
                </p>
                <ul className={s.completedList}>
                  {completedBatchesList.map((b) => {
                    const p = progress[b.id] || {};
                    const { total, actioned, pending } = completedBatchCounts(p, b);
                    const isBot = String(b.completion_source || '').toLowerCase() === 'marketing_bot';
                    const dateLabel = p.ops_completed_at || b.escalated_at || b.created_at;
                    return (
                      <li key={b.id}>
                        <button type="button" className={s.completedItem} onClick={() => openCompletedBatch(b)}>
                          <div className={s.completedItemInner}>
                            <div className={s.completedItemMain}>
                              <div className={s.completedItemTitle}>
                                <span className={s.batchId}>#{b.id}</span>
                                <span className={s.batchName}>{b.name || 'Batch'}</span>
                                {isBot ? (
                                  <span className={`${s.batchBotBadge} ${s.batchBotBadgeInline}`} title="Escalated from Marketing bot">
                                    <i className="fa fa-android" aria-hidden />
                                    <span>Bot</span>
                                  </span>
                                ) : null}
                              </div>
                              <CompletedBatchCountLine total={total} actioned={actioned} pending={pending} />
                              {dateLabel ? (
                                <div className={s.completedItemDate}>
                                  {formatMemberTimestamp(dateLabel)}
                                </div>
                              ) : null}
                            </div>
                            <div className={s.completedItemStats}>
                              <CompletedBatchOutcomeStats progress={p} compact />
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className={s.completedPager}>
                  <button type="button" className={s.pagerBtn} disabled={completedBatchPage <= 1} onClick={() => loadCompletedBatches(completedBatchPage - 1)}>Prev</button>
                  <span>
                    Batch page {completedBatchPage} of {completedBatchMeta.total_pages || 1}
                  </span>
                  <button
                    type="button"
                    className={s.pagerBtn}
                    disabled={completedBatchPage >= (completedBatchMeta.total_pages || 1)}
                    onClick={() => loadCompletedBatches(completedBatchPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </>
            ) : completedMembersLoading ? (
              <div className={s.loading}>Loading members…</div>
            ) : (
              <>
                {completedViewBatch ? (
                  <div className={s.completedDetailCompact}>
                    <CompletedBatchSummaryRow
                      progress={completedViewProgress}
                      batch={completedViewBatch}
                      showTitle={false}
                      showOutcomes={false}
                    >
                      <div className={s.memberFilterBarMain}>
                        <CompletedOutcomeFilterBar
                          progress={completedViewProgress}
                          activeFilter={completedOutcomeFilter}
                          onSelect={(key) => openCompletedBatch(completedViewBatch, 1, key)}
                        />
                        <div className={s.memberFilterBarActions}>
                          <RefollowSelectToolbar
                            selectMode={refollowSelectMode}
                            selectedCount={refollowSelectedCount}
                            checkAllBusy={refollowCheckAllBusy}
                            onStart={() => {
                              setRefollowSelectMode(true);
                              clearRefollowSelection();
                            }}
                            onCheckAll={selectAllRefollowEligible}
                            onCheckPage={selectAllRefollowOnPage}
                            onCreate={() => setRefollowModalOpen(true)}
                            onCancel={() => {
                              setRefollowSelectMode(false);
                              clearRefollowSelection();
                            }}
                          />
                        </div>
                      </div>
                    </CompletedBatchSummaryRow>
                  </div>
                ) : null}
                <div className={`${s.completedMemberPager} ${s.completedMemberPagerCompact}`}>
                  <button
                    type="button"
                    className={s.pagerBtn}
                    disabled={completedMembersPage <= 1 || completedMembersLoading}
                    onClick={() => openCompletedBatch(
                      completedViewBatch,
                      completedMembersPage - 1,
                      completedOutcomeFilter,
                    )}
                  >
                    Prev
                  </button>
                  <span className={s.completedMemberPagerLabel}>
                    Page {completedMembersPage} of {completedMemberMeta.total_pages || 1}
                    {' · '}
                    {completedMemberMeta.total || 0} member
                    {(completedMemberMeta.total || 0) === 1 ? '' : 's'}
                    {completedOutcomeFilter !== 'all' ? ' (filtered)' : ''}
                  </span>
                  <button
                    type="button"
                    className={s.pagerBtn}
                    disabled={
                      completedMembersLoading
                      || completedMembersPage >= (completedMemberMeta.total_pages || 1)
                    }
                    onClick={() => openCompletedBatch(
                      completedViewBatch,
                      completedMembersPage + 1,
                      completedOutcomeFilter,
                    )}
                  >
                    Next
                  </button>
                </div>
                <div className={s.completedMemberList}>
                  {completedMembers.length === 0 ? (
                    <div className={s.empty}>
                      {completedOutcomeFilter !== 'all'
                        ? 'No members with this outcome on this page.'
                        : 'No members in this batch.'}
                    </div>
                  ) : (
                    completedMembers.map((m) => {
                      const mid = m.id || m.batch_member_id;
                      return (
                        <CallManagerReadOnlyMemberRow
                          key={m.id || m.phone_e164}
                          member={m}
                          batch={completedViewBatch}
                          refollowSelectMode={refollowSelectMode}
                          refollowSelected={refollowSelectedIds.has(mid)}
                          onToggleRefollowSelect={toggleRefollowSelect}
                        />
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      <CapturedWhatsappModal
        isOpen={!!nwCaptureModal}
        onClose={() => { if (!nwCaptureBusy) setNwCaptureModal(null); }}
        dialedPhone={nwCaptureModal?.member?.phone_e164 || nwCaptureModal?.member?.phone || ''}
        initialCaptured={nwCaptureModal?.member?.captured_whatsapp_number || ''}
        busy={nwCaptureBusy}
        onConfirm={async (wa) => {
          if (!nwCaptureModal?.member || !nwCaptureModal?.batch) return;
          setNwCaptureBusy(true);
          try {
            await runCmNwQuickCreate(nwCaptureModal.member, nwCaptureModal.batch, wa);
            setNwCaptureModal(null);
          } catch (err) {
            showToast(err.message || 'Create customer failed', 'error');
          } finally {
            setNwCaptureBusy(false);
          }
        }}
      />

      <BatchCompleteConfirmModal
        open={completeModalOpen}
        progress={selectedBatchProgress}
        confirming={completeConfirming}
        onCancel={() => { if (!completeConfirming) setCompleteModalOpen(false); }}
        onConfirm={confirmBatchComplete}
      />

      <CreateRefollowModal
        open={refollowModalOpen}
        onClose={() => { if (!refollowSubmitting) setRefollowModalOpen(false); }}
        selectedMembers={refollowSelectedMembers}
        selectedCount={refollowSelectedCount}
        sourceBatchId={refollowSourceBatchId}
        onSubmit={submitRefollowBatch}
        submitting={refollowSubmitting}
      />
    </div>
  );
};

export default CallManagerPage;
